import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Square,
  Paperclip,
  Search,
  Plus,
  Edit3,
  Trash2,
  Book,
  FileText,
  ChevronDown,
  X,
  Copy,
  CornerUpLeft,
  LogOut,
  Moon,
  Sun,
  Check,
  Grid,
  Zap,
  MessageSquare,
  Layers,
  Menu,
  Edit,
  BrainCircuit,
  PenLine,
  UploadCloud,
  Loader2,
  BookOpen,
} from "lucide-react";
// ThemeToggle import removed - using context instead
import { motion, AnimatePresence } from "framer-motion";
import "katex/dist/katex.min.css";
import TestParameterModal from "./modals/TestParameterModal.jsx";
import NotesTab from "./NotesTab.jsx";
import QuizDeck from "./QuizDeck.jsx";
import EnhancedQuizDeck from "./EnhancedQuizDeck.jsx";
import FlashcardDeck from "./FlashcardDeck.jsx";
import TestMode from "./TestMode.jsx";
import ResourcesSidebar from "./ResourcesSidebar";
import ChatMarkdown from "./ChatMarkdown.jsx";
import Modal from "./Modal";
import QuizIntegration from "./QuizIntegration";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { useAssignment } from "../context/AssignmentContext";
import { useSidePanel } from "../context/SidePanelContext";
import { useGeneratedContent } from "../context/GeneratedContentContext";
import { useActiveNote } from "../context/ActiveNoteContext";
import styles from "../styles/home.module.css";
import "../styles/theme.css";
import aiStarLogo from "../assets/img/ai-star.png";
import AssignmentSetupModal from "./modals/AssignmentSetupModal.jsx";
import AssignmentBuilder from "./AssignmentBuilder.jsx";
import TestContainer from "./TestContainer.jsx";
import DocumentLibrary from "./DocumentLibrary.jsx";
import CommandPalette from "./CommandPalette.jsx";
import ContentSidePanel from "./ContentSidePanel.jsx";
import StudyNotesView from "./StudyNotesView.jsx";
import QuotesView from "./QuotesView.jsx";
import LayoutSelector from "./LayoutSelector.jsx";
import QuadrantLayout from "./QuadrantLayout.jsx";
import { useLayout, LAYOUTS } from "../context/LayoutContext";
import { useNotebookSidebar } from "../context/NotebookSidebarContext";
import { useWorkspace } from "../context/WorkspaceContext";
import DocumentCanvas from "./DocumentCanvas";
import { getFilesFromStorage } from "../utils/fileStorage";
import { detectIntent } from "../utils/intent";
import LiquidMetalButton from "./LiquidMetalButton.jsx";
import iconStyles from "../styles/iconAnimations.module.css";
import NotebookPage from "../views/notebook";
import TrialBanner from "./TrialBanner";
import PdfHighlightReader from "./PdfHighlightReader";
import HighlightExplainerPanel from "./HighlightExplainerPanel";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5001";

// Icon helper
const renderIcon = (name, size = 18) => {
  const map = {
    "add-circle": Plus,
    "chat-bubble": MessageSquare,
    "upload-cloud": Paperclip,
    "file-check": FileText,
    lightning: Zap,
  };
  const Icon = map[name] || Square;
  return <Icon size={size} />;
};

const LOCAL_API_URLS = {
  CHAT: `${API_BASE}/api/chat`,
  GENERATE: `${API_BASE}/api/generate`,
  GENERATE_EXAM: `${API_BASE}/api/generate_exam`,
  GENERATE_ASSIGNMENT: `${API_BASE}/api/generate_assignment`,
  EXTRACT_CITATION: `${API_BASE}/api/extract_citation`,
  PROCESS_DOCUMENT: `${API_BASE}/api/process_document`,
  ANALYZE_ASSIGNMENT: `${API_BASE}/api/analyze_assignment`,
  ENHANCED_GENERATE: `${API_BASE}/api/enhanced_generate`,
  ENHANCED_PARSE: `${API_BASE}/api/enhanced_parse`,
  SMART_SUGGESTIONS: `${API_BASE}/api/smart_suggestions`,
  CONVERSATION_SUMMARY: `${API_BASE}/api/conversation_summary`,
  CONTEXT_ANALYSIS: `${API_BASE}/api/context_analysis`,
};

// Flag to use enhanced parser for study tools
const USE_ENHANCED_PARSER = true;

// Module-scope quiz validator so it can be used by both the Message renderer and
// the send/receive flow below.
// Debug toggle: set localStorage.QUIZ_DEBUG = '1' to enable verbose logs
const QUIZ_DEBUG = (() => {
  try {
    return localStorage.getItem("QUIZ_DEBUG") === "1";
  } catch {
    return false;
  }
})();
const dbg = (...args) => {
  if (QUIZ_DEBUG) console.debug("[Quiz]", ...args);
};

const validateQuizQuality = (imm, { topic = "", minPassRatio = 0.8 } = {}) => {
  try {
    if (!imm || !Array.isArray(imm.questions) || imm.questions.length === 0)
      return false;
    const genericRe =
      /(related to the concept|this question|placeholder|choose the correct|not related|unrelated)/i;
    let pass = 0;
    const issues = [];
    for (const q of imm.questions) {
      const choices = Array.isArray(q.choices) ? q.choices : [];
      const answers = Array.isArray(q.correctAnswerIds)
        ? q.correctAnswerIds
        : q.correctAnswers || [];
      const okLen = choices.length === 4;
      const uniq =
        new Set(choices.map((c) => String(c).trim().toLowerCase())).size === 4;
      const hasAnswer = (answers || []).length === 1; // enforce single-correct for consistency with player
      const notGeneric =
        choices.every((c) => !genericRe.test(String(c))) &&
        !genericRe.test(String(q.prompt || q.question || ""));
      let contradictionFree = true;
      try {
        // Contradiction check: if explanation mentions a non-correct choice explicitly, reject
        const correctIdx = Number.isFinite((answers || [])[0])
          ? Number((answers || [])[0])
          : null;
        if (
          correctIdx !== null &&
          correctIdx >= 0 &&
          correctIdx < choices.length
        ) {
          const expl =
            String(q.explanation || "") +
            " " +
            String((q.choiceRationales || [])[correctIdx] || "");
          const explLower = expl.toLowerCase();
          const correctText = String(choices[correctIdx] || "").toLowerCase();
          const mentionsCorrect =
            correctText && explLower.includes(correctText);
          for (let i = 0; i < choices.length; i++) {
            if (i === correctIdx) continue;
            const other = String(choices[i] || "").toLowerCase();
            if (other && explLower.includes(other) && !mentionsCorrect) {
              contradictionFree = false;
              break;
            }
          }
        }
      } catch {}

      // Ensure per-choice rationales array aligns; only correct index may have non-empty text
      let rationaleAligned = true;
      try {
        const cr = Array.isArray(q.choiceRationales) ? q.choiceRationales : [];
        if (cr.length && cr.length !== choices.length) rationaleAligned = false;
        const correctIdx = Number.isFinite((answers || [])[0])
          ? Number((answers || [])[0])
          : null;
        if (cr.length && correctIdx !== null) {
          cr.forEach((r, idx) => {
            const txt = String(r || "").trim();
            if (idx === correctIdx) {
              // allow either empty or descriptive
            } else {
              if (txt.length > 0) rationaleAligned = false;
            }
          });
        }
      } catch {}

      const ok =
        okLen &&
        uniq &&
        hasAnswer &&
        notGeneric &&
        contradictionFree &&
        rationaleAligned;
      if (!ok) {
        const why = [];
        if (!okLen) why.push("not-4-choices");
        if (!uniq) why.push("duplicate-choices");
        if (!hasAnswer) why.push("no-single-correct");
        if (!notGeneric) why.push("generic-content");
        if (!contradictionFree) why.push("contradiction-explanation");
        if (!rationaleAligned) why.push("rationales-misaligned");
        issues.push({ prompt: q.prompt || q.question || "", why });
      } else {
        pass++;
      }
    }
    const ratio = pass / imm.questions.length;
    const okAll = ratio >= minPassRatio;
    if (!okAll)
      dbg("Validation failed", {
        topic,
        pass,
        total: imm.questions.length,
        ratio,
        minPassRatio,
        issues,
      });
    else
      dbg("Validation passed", {
        topic,
        pass,
        total: imm.questions.length,
        ratio,
      });
    return okAll;
  } catch {
    return false;
  }
};

// Ask backend LLM to critique and fix a fenced quiz JSON block to conform to our rules.
// Returns corrected assistant text containing a fenced quiz, or empty string on failure.
async function critiqueAndFixQuiz(rawText, topic) {
  try {
    const fenceMatch = (rawText || "").match(/```\s*quiz\s*\n([\s\S]*?)```/i);
    if (!fenceMatch) return "";
    const jsonStr = fenceMatch[1];
    const prompt = `You are a strict quiz validator and fixer. Review the following quiz JSON and FIX any problems.

REWRITE the quiz to satisfy ALL rules:
- Exactly 4 choices per question
- Single correct answer per question (0-based index in correctAnswerIds)
- Remove generic/filler content
- Provide concise explanation at question level
- choiceRationales same length as choices; only the correct index may have non-empty text
- Valid JSON inside a fenced code block labeled quiz; return ONLY the fenced JSON.

TOPIC: ${topic || "General"}

Here is the quiz JSON to fix:
\n\n\`\`\`quiz\n${jsonStr}\n\`\`\``;

    const res = await fetch(LOCAL_API_URLS.CHAT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message: prompt, history: [], stream: false }),
    });
    const data = await res.json();
    const text = data?.reply || data?.content || data?.text || "";
    return typeof text === "string" ? text : "";
  } catch {
    return "";
  }
}

// Simple loading/error card shown inside the side panel while preparing the quiz
const QuizPreparingCard = ({ text = "Preparing…" }) => {
  const isError = typeof text === "string" && /^error:/i.test(text.trim());
  const title = isError ? "Quiz unavailable" : "Preparing quiz…";
  const body = isError ? text.replace(/^error:/i, "").trim() : text;
  return (
    <div style={{ padding: 12 }}>
      <div
        style={{
          border: "1px solid var(--border-color,#2b3442)",
          background: "var(--card-bg,#0f172a)",
          borderRadius: 12,
          padding: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          color: "#e5e7eb",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: "#1f2937",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
            }}
          >
            {isError ? "⚠️" : "⏳"}
          </div>
          <div>
            <div style={{ fontWeight: 700 }}>{title}</div>
            <div style={{ fontSize: 12, color: "var(--muted,#9ca3af)" }}>
              {body}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Removed unused ResponseTypeDropdown component

// Sample data for the chat interface

// Chart.js registration removed - using ChatMarkdown for enhanced rendering

// Quotes Preview Component
const QuotesPreview = ({ message, onOpenImmersive }) => {
  const quotesCount = message.quotesData?.quotes?.length || 0;

  return (
    <div className={styles.assistantContent}>
      <div
        style={{
          border: "1px solid var(--border)",
          background: "var(--bg-secondary)",
          borderRadius: 8,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: "0.95rem",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Book size={16} />
          {quotesCount} Key Quotes - {message.documentName || "Document"}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => {
              onOpenImmersive({
                document: message.documentInfo || {
                  name: message.documentName || "Document",
                },
                contentType: "quotes",
                content: message.quotesData,
              });
            }}
            style={{
              background: "var(--foreground)",
              color: "var(--background)",
              border: "none",
              borderRadius: 6,
              padding: "6px 16px",
              fontWeight: 500,
              fontSize: "0.875rem",
              cursor: "pointer",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.target.style.opacity = "1")}
          >
            View Quotes
          </button>
        </div>
      </div>
    </div>
  );
};

// Notes Preview Component
const NotesPreview = ({ message, onOpenImmersive }) => {
  const [showInChat, setShowInChat] = useState(false);

  return (
    <div className={styles.assistantContent}>
      <div
        style={{
          border: "1px solid var(--border)",
          background: "var(--bg-secondary)",
          borderRadius: 8,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <div
          style={{
            fontWeight: 600,
            fontSize: "0.95rem",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <FileText size={16} />
          {message.notesTitle || "Study Notes"}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => setShowInChat(true)}
            style={{
              background: "transparent",
              color: "var(--foreground)",
              border: "1px solid var(--border)",
              borderRadius: 6,
              padding: "6px 12px",
              fontWeight: 500,
              fontSize: "0.875rem",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.target.style.background = "rgba(255, 255, 255, 0.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.background = "transparent";
            }}
          >
            View in Chat
          </button>
          <button
            onClick={() => {
              onOpenImmersive({
                document: message.documentInfo || {
                  name: message.notesTitle || "Study Notes",
                },
                contentType: "notes",
                content: message.notesData,
              });
            }}
            style={{
              background: "var(--foreground)",
              color: "var(--background)",
              border: "none",
              borderRadius: 6,
              padding: "6px 16px",
              fontWeight: 500,
              fontSize: "0.875rem",
              cursor: "pointer",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
            onMouseLeave={(e) => (e.target.style.opacity = "1")}
          >
            Open Side Panel
          </button>
        </div>
      </div>
      {showInChat && (
        <div
          style={{
            marginTop: 12,
            padding: "20px",
            background: "var(--bg-tertiary)",
            borderRadius: "8px",
            border: "1px solid var(--border)",
            fontSize: "14px",
            lineHeight: "1.7",
          }}
        >
          <ChatMarkdown content={message.content} />
        </div>
      )}
    </div>
  );
};

// Message component for chat interface
const Message = ({
  message,
  onQuizComplete,
  refillInput,
  onContinue,
  onOpenImmersive,
  onInsertToNote,
}) => {
  const [showCopied, setShowCopied] = useState(false);
  const { openComponentTab } = useSidePanel();
  // Side-panel quiz tab title (normalized to ensure strict de-dup across reopens)
  const QUIZ_TAB_TITLE = "Quiz";

  const variants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
  };

  // If this is a status message, use it to open/update the side-panel loading tab
  useEffect(() => {
    // Disabled automatic status tab opening to prevent "Preparing quiz..." tabs
    // if (message?.type === 'status') {
    //   dbg('STATUS update', { text: message.content });
    //   openComponentTab(
    //     <QuizPreparingCard text={message.content || 'Preparing…'} />,
    //     'Quiz'
    //   );
    // }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message?.type, message?.content]);

  // Do not return early; hooks must preserve order. We'll render a hidden placeholder later.

  // Basic validator is available at module scope as validateQuizQuality

  // Parse a plain fenced quiz block without <immersive>
  const parseFencedQuiz = (content) => {
    try {
      if (!content) return null;
      const fenceMatch = content.match(/```\s*quiz\s*\n([\s\S]*?)```/i);
      if (!fenceMatch) return null;
      const jsonStr = fenceMatch[1];
      const obj = JSON.parse(jsonStr);
      if (!obj || !Array.isArray(obj.questions)) return null;
      return obj;
    } catch (e) {
      return null;
    }
  };

  // Map immersive fenced quiz JSON to Gemini quiz schema
  const mapImmersiveToGemini = (imm) => {
    try {
      if (!imm || !Array.isArray(imm.questions)) return null;
      const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      const questions = imm.questions.map((q, idx) => {
        let answers = Array.isArray(q.correctAnswerIds)
          ? q.correctAnswerIds
          : q.correctAnswers || [];
        answers = (answers || []).map(Number).filter((n) => Number.isFinite(n));
        const choices = Array.isArray(q.choices) ? q.choices : [];
        // Normalization rule:
        // - If max index equals choices.length, it's almost certainly 1-based → subtract 1 for all.
        // - Otherwise assume 0-based and do not shift.
        if (answers.length && choices.length) {
          const maxIdx = Math.max(...answers);
          if (maxIdx === choices.length) {
            answers = answers.map((n) => n - 1);
          }
        }
        // Clamp to range
        answers = answers.filter((n) => n >= 0 && n < choices.length);
        // Enforce single-correct; if multiple provided, keep the first
        if (answers.length > 1) answers = [answers[0]];
        let correctIdx = answers.length === 1 ? answers[0] : null;

        // Auto-correct contradiction: if explanation mentions another option and not the current correct, switch
        try {
          if (choices.length) {
            const expl = String(q.explanation || "").toLowerCase();
            const cr = Array.isArray(q.choiceRationales)
              ? q.choiceRationales
              : [];
            const correctText =
              correctIdx != null
                ? String(choices[correctIdx] || "").toLowerCase()
                : "";
            const mentionsCorrect = correctText && expl.includes(correctText);
            if (!mentionsCorrect) {
              // find best matching option by longest token overlap in explanation
              let bestIdx = correctIdx;
              let bestScore = -1;
              choices.forEach((c, i) => {
                const t = String(c || "").toLowerCase();
                if (!t) return;
                let score = 0;
                if (expl.includes(t)) score += t.length;
                const rat = String(cr[i] || "").toLowerCase();
                if (rat.includes(t)) score += t.length / 2;
                if (score > bestScore) {
                  bestScore = score;
                  bestIdx = i;
                }
              });
              if (bestIdx != null && bestIdx !== correctIdx && bestScore > 0) {
                correctIdx = bestIdx;
              }
            }
          }
        } catch {}

        const correctIdxSet = new Set(correctIdx != null ? [correctIdx] : []);
        const rawChoiceRats = Array.isArray(q.choiceRationales)
          ? q.choiceRationales
          : [];
        // Align rationales array length with choices; only correct gets non-empty
        const choiceRationales = choices.map((_, i) => {
          const isCorrect = correctIdxSet.has(i);
          if (isCorrect) {
            const rat = rawChoiceRats[i];
            return typeof rat === "string" ? rat : q.explanation || "";
          }
          return "";
        });
        const answerOptions = choices.map((t, i) => ({
          id: `${idx}_${i}`,
          text: t,
          isCorrect: correctIdxSet.has(i),
          rationale: choiceRationales[i],
        }));
        return {
          id: q.id || `${idx + 1}`,
          question: q.prompt || q.question || `Question ${idx + 1}`,
          answerOptions,
          hint: q.hint || null,
          rationale: q.explanation || "",
        };
      });
      return {
        title: imm.title || "Practice Quiz",
        settings: {
          shuffle: false,
          showHints: true,
          showPerQuestionFeedback: true,
        },
        questions,
      };
    } catch (e) {
      return null;
    }
  };

  // Map existing quizUtils shape to Gemini quiz schema
  const mapLegacyQuizToGemini = (legacy) => {
    if (!legacy || !Array.isArray(legacy.cards)) return null;
    const questions = legacy.cards.map((c, idx) => {
      const correctText = c.answer;
      const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      return {
        id: c.id || `q_${idx + 1}`,
        question: c.question,
        answerOptions: (c.options || []).map((opt, i) => ({
          id: letters[i] || String(i + 1),
          text: opt,
          isCorrect: opt === correctText,
          rationale: opt === correctText ? c.explanation || "" : "",
        })),
        hint: undefined,
        rationale: c.explanation || "",
      };
    });
    return {
      title: legacy.title || "Practice Quiz",
      settings: {
        shuffle: false,
        showHints: true,
        showPerQuestionFeedback: true,
      },
      questions,
    };
  };

  // Simple detector for <immersive> ```quiz JSON``` blocks
  const parseImmersiveQuiz = (content) => {
    try {
      if (!content || !content.includes("<immersive")) return null;
      const immersiveMatch = content.match(/<immersive>[\s\S]*?<\/immersive>/i);
      if (!immersiveMatch) return null;
      const block = immersiveMatch[0];
      const fenceMatch = block.match(/```\s*quiz\s*\n([\s\S]*?)```/i);
      if (!fenceMatch) return null;
      const jsonStr = fenceMatch[1];
      const obj = JSON.parse(jsonStr);
      if (!obj || !Array.isArray(obj.questions)) return null;
      return obj;
    } catch (e) {
      return null;
    }
  };

  // Function to intelligently detect and enhance content with visualizations
  const enhanceContentWithVisualizations = (content) => {
    if (!content) return "";

    // Use simpler regex-based approach that works reliably in the browser
    let enhancedContent = content;

    // Process tables with proper CSS classes and scrolling wrapper
    if (content.includes("<table>")) {
      // Wrap tables in scrolling container
      enhancedContent = enhancedContent.replace(
        /<table([^>]*)>/g,
        `<div class="${styles.tableScrollContainer}"><table $1 class="${styles.customTable}">`,
      );
      enhancedContent = enhancedContent.replace(/<\/table>/g, `</table></div>`);

      // Table headers
      enhancedContent = enhancedContent.replace(
        /<th([^>]*)>/g,
        `<th $1 class="${styles.tableHeaderCell}">`,
      );

      // Table rows
      enhancedContent = enhancedContent.replace(
        /<tr([^>]*)>/g,
        `<tr $1 class="${styles.tableRow}">`,
      );

      // Table cells
      enhancedContent = enhancedContent.replace(
        /<td([^>]*)>/g,
        `<td $1 class="${styles.tableCell}">`,
      );
    }

    // Process code blocks
    if (content.includes("<pre>") || content.includes("<code>")) {
      enhancedContent = enhancedContent.replace(
        /<pre><code([^>]*)>/g,
        `<pre class="${styles.codeBlock}"><code$1>`,
      );
    }

    // Process markdown code blocks
    enhancedContent = enhancedContent.replace(
      /```([a-z]*)\n([\s\S]*?)```/g,
      `<pre class="${styles.codeBlock}"><code>$2</code></pre>`,
    );

    // Process blockquotes
    if (content.includes("<blockquote>")) {
      enhancedContent = enhancedContent.replace(
        /<blockquote([^>]*)>/g,
        `<blockquote $1 class="${styles.blockquote}">`,
      );
    }

    // Process markdown blockquotes
    enhancedContent = enhancedContent.replace(
      /^&gt;\s(.*)$/gm,
      `<blockquote class="${styles.blockquote}">$1</blockquote>`,
    );

    // Process headings
    ["h1", "h2", "h3", "h4", "h5", "h6"].forEach((tag) => {
      enhancedContent = enhancedContent.replace(
        new RegExp(`<${tag}([^>]*)>`, "g"),
        `<${tag} $1 class="${styles.messageHeading}">`,
      );
    });

    // Process paragraphs
    enhancedContent = enhancedContent.replace(
      /<p([^>]*)>/g,
      `<p $1 class="${styles.messageParagraph}">`,
    );

    // Process lists
    enhancedContent = enhancedContent.replace(
      /<ul([^>]*)>/g,
      `<ul $1 class="${styles.messageList}">`,
    );

    enhancedContent = enhancedContent.replace(
      /<ol([^>]*)>/g,
      `<ol $1 class="${styles.messageList}">`,
    );

    enhancedContent = enhancedContent.replace(
      /<li([^>]*)>/g,
      `<li $1 class="${styles.messageListItem}">`,
    );

    // Chemical formula and reaction detection disabled to prevent unwanted boxes around regular text
    // We'll only style chemical formulas and reactions that are explicitly marked in specific demos

    return enhancedContent;
  };

  // Pre-parse immersive or fenced quiz if present (must be before any early returns for hooks order)
  const immersiveQuiz = parseImmersiveQuiz(message.content);
  const fencedQuiz = immersiveQuiz ? null : parseFencedQuiz(message.content);

  // Quiz opens only when user clicks "Start Quiz" — no auto-open

  // For system messages (like topic changes)
  if (message.type === "system") {
    return (
      <motion.div
        id={`message-${message.id}`}
        variants={variants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.2 }}
        className={styles.systemMessageWrapper}
      >
        <div className={styles.systemMessage}>
          <div className={styles.systemMessageContent}>{message.content}</div>
        </div>
      </motion.div>
    );
  }

  // For user messages
  if (message.type === "user") {
    const handleRefill = () => refillInput && refillInput(message.content);

    return (
      <motion.div
        key={message.id}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={{ duration: 0.2 }}
        className={styles.userMessageWrapper}
        data-message-type="user"
        data-message-id={message.id}
      >
        <div className={styles.userMessageContainer}>
          <div className={styles.userBubble}>{message.content}</div>
          <CornerUpLeft
            className={`${styles.messageActionIcon} ${iconStyles.iconHoverRotate}`}
            onClick={handleRefill}
            title="Edit and resend"
            data-label="Edit"
          />
        </div>
      </motion.div>
    );
  }

  // Copy to clipboard helper
  const handleCopy = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(
        typeof message.content === "string"
          ? message.content
          : JSON.stringify(message.content),
      );
      // Show copied message
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    }
  };

  // Copy code block helper
  const handleCodeCopy = (code) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(code);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    }
  };

  // Render visualization components
  const renderVisualization = (vizData) => {
    const { type, data, title, description } = vizData;

    switch (type) {
      case "bar-chart":
        return renderBarChart(data, title, description);
      case "line-chart":
        return renderLineChart(data, title, description);
      case "pie-chart":
        return renderPieChart(data, title, description);
      case "table":
        return renderEnhancedTable(data, title, description);
      case "diagram":
        return renderDiagram(data, title, description);
      default:
        return <div>Unsupported visualization type: {type}</div>;
    }
  };

  // Bar Chart Component
  const renderBarChart = (data, title, description) => {
    const maxValue = Math.max(...data.values);
    return (
      <div className={styles.visualizationContainer}>
        {title && <h3 className={styles.vizTitle}>{title}</h3>}
        {description && <p className={styles.vizDescription}>{description}</p>}
        <div className={styles.barChart}>
          <svg width="100%" height="300" viewBox="0 0 400 300">
            <defs>
              <linearGradient
                id="barGradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#60a5fa", stopOpacity: 0.8 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#3b82f6", stopOpacity: 0.6 }}
                />
              </linearGradient>
            </defs>
            {data.labels.map((label, index) => {
              const barHeight = (data.values[index] / maxValue) * 200;
              const x = 50 + index * (300 / data.labels.length);
              return (
                <g key={index}>
                  <rect
                    x={x}
                    y={250 - barHeight}
                    width={30}
                    height={barHeight}
                    fill="url(#barGradient)"
                    rx={4}
                  />
                  <text
                    x={x + 15}
                    y={270}
                    textAnchor="middle"
                    fill="#60a5fa"
                    fontSize="12"
                  >
                    {label}
                  </text>
                  <text
                    x={x + 15}
                    y={240 - barHeight}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.9)"
                    fontSize="11"
                  >
                    {data.values[index]}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  // Enhanced Table Component
  const renderEnhancedTable = (data, title, description) => {
    return (
      <div className={styles.visualizationContainer}>
        {title && <h3 className={styles.vizTitle}>{title}</h3>}
        {description && <p className={styles.vizDescription}>{description}</p>}
        <div className={styles.tableScrollWrapper}>
          <table className={styles.enhancedTable}>
            <thead>
              <tr>
                {data.headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Line Chart Component
  const renderLineChart = (data, title, description) => {
    const maxValue = Math.max(...data.values);
    const minValue = Math.min(...data.values);
    const range = maxValue - minValue || 1;

    const points = data.values
      .map((value, index) => {
        const x = 50 + (index * 300) / (data.values.length - 1);
        const y = 250 - ((value - minValue) / range) * 200;
        return `${x},${y}`;
      })
      .join(" ");

    return (
      <div className={styles.visualizationContainer}>
        {title && <h3 className={styles.vizTitle}>{title}</h3>}
        {description && <p className={styles.vizDescription}>{description}</p>}
        <div className={styles.lineChart}>
          <svg width="100%" height="300" viewBox="0 0 400 300">
            <defs>
              <linearGradient
                id="lineGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#60a5fa", stopOpacity: 0.8 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#34d399", stopOpacity: 0.8 }}
                />
              </linearGradient>
            </defs>
            <polyline
              points={points}
              fill="none"
              stroke="url(#lineGradient)"
              strokeWidth="3"
              strokeLinecap="round"
            />
            {data.values.map((value, index) => {
              const x = 50 + (index * 300) / (data.values.length - 1);
              const y = 250 - ((value - minValue) / range) * 200;
              return (
                <g key={index}>
                  <circle cx={x} cy={y} r="4" fill="#60a5fa" />
                  <text
                    x={x}
                    y={280}
                    textAnchor="middle"
                    fill="#60a5fa"
                    fontSize="12"
                  >
                    {data.labels[index]}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>
      </div>
    );
  };

  // Pie Chart Component
  const renderPieChart = (data, title, description) => {
    const total = data.values.reduce((sum, value) => sum + value, 0);
    let currentAngle = 0;

    return (
      <div className={styles.visualizationContainer}>
        {title && <h3 className={styles.vizTitle}>{title}</h3>}
        {description && <p className={styles.vizDescription}>{description}</p>}
        <div className={styles.pieChart}>
          <svg width="100%" height="300" viewBox="0 0 400 300">
            <g transform="translate(200, 150)">
              {data.values.map((value, index) => {
                const angle = (value / total) * 360;
                const startAngle = currentAngle;
                const endAngle = currentAngle + angle;
                currentAngle += angle;

                const startAngleRad = (startAngle * Math.PI) / 180;
                const endAngleRad = (endAngle * Math.PI) / 180;

                const x1 = Math.cos(startAngleRad) * 80;
                const y1 = Math.sin(startAngleRad) * 80;
                const x2 = Math.cos(endAngleRad) * 80;
                const y2 = Math.sin(endAngleRad) * 80;

                const largeArcFlag = angle > 180 ? 1 : 0;

                const pathData = [
                  `M 0 0`,
                  `L ${x1} ${y1}`,
                  `A 80 80 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                  "Z",
                ].join(" ");

                const colors = [
                  "#60a5fa",
                  "#34d399",
                  "#f59e0b",
                  "#ef4444",
                  "#8b5cf6",
                ];

                return (
                  <path
                    key={index}
                    d={pathData}
                    fill={colors[index % colors.length]}
                    opacity={0.8}
                  />
                );
              })}
            </g>
          </svg>
        </div>
      </div>
    );
  };

  // Diagram Component
  const renderDiagram = (data, title, description) => {
    return (
      <div className={styles.visualizationContainer}>
        {title && <h3 className={styles.vizTitle}>{title}</h3>}
        {description && <p className={styles.vizDescription}>{description}</p>}
        <div className={styles.diagram}>
          <svg width="100%" height="200" viewBox="0 0 400 200">
            <defs>
              <linearGradient
                id="diagramGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="0%"
              >
                <stop
                  offset="0%"
                  style={{ stopColor: "#60a5fa", stopOpacity: 0.8 }}
                />
                <stop
                  offset="100%"
                  style={{ stopColor: "#34d399", stopOpacity: 0.8 }}
                />
              </linearGradient>
            </defs>
            <path
              d="M 50 150 Q 200 50 350 150"
              fill="none"
              stroke="url(#diagramGradient)"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <circle cx="200" cy="100" r="6" fill="#ffffff" />
            <text
              x="50"
              y="180"
              fill="#60a5fa"
              fontSize="14"
              textAnchor="middle"
            >
              {data.start || "Start"}
            </text>
            <text
              x="350"
              y="180"
              fill="#60a5fa"
              fontSize="14"
              textAnchor="middle"
            >
              {data.end || "End"}
            </text>
          </svg>
        </div>
      </div>
    );
  };

  // For assistant messages
  return (
    <motion.div
      key={message.id}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.2 }}
      className={styles.assistantMessageWrapper}
      data-message-id={message.id}
    >
      {message?.type === "status" ? (
        <div style={{ display: "none" }} />
      ) : (
        <div className={styles.assistantMessageContainer}>
          {/* Message Content with Animation */}
          <motion.div
            initial={{ clipPath: "inset(0 100% 0 0)" }}
            animate={{ clipPath: "inset(0 0% 0 0)" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            style={{
              flex: 1,
              overflow: "hidden",
            }}
          >
            {message.quotesData ? (
              <QuotesPreview
                message={message}
                onOpenImmersive={onOpenImmersive}
              />
            ) : message.notesData ? (
              <NotesPreview
                message={message}
                onOpenImmersive={onOpenImmersive}
              />
            ) : immersiveQuiz || fencedQuiz ? (
              <div className={styles.assistantContent}>
                <div
                  style={{
                    border: "1px solid var(--border)",
                    background: "var(--bg-secondary)",
                    borderRadius: 8,
                    padding: "12px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                    {(immersiveQuiz || fencedQuiz)?.title || "Quiz"}
                  </div>
                  <button
                    onClick={() => {
                      const imm = immersiveQuiz || fencedQuiz;
                      const gem = mapImmersiveToGemini(imm);
                      if (gem && gem.questions?.length) {
                        openComponentTab(
                          <EnhancedQuizDeck
                            quiz={gem}
                            onComplete={() => {
                              /* keep results inside player */
                            }}
                            onExit={() => {
                              /* panel needs closable UI */
                            }}
                          />,
                          gem?.title || "Quiz",
                        );
                        setTimeout(
                          () =>
                            window.dispatchEvent(
                              new CustomEvent("closeSidebar"),
                            ),
                          50,
                        );
                      }
                    }}
                    style={{
                      background: "var(--foreground)",
                      color: "var(--background)",
                      border: "none",
                      borderRadius: 6,
                      padding: "6px 16px",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      transition: "opacity 0.2s",
                    }}
                    onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
                    onMouseLeave={(e) => (e.target.style.opacity = "1")}
                  >
                    Start Quiz
                  </button>
                </div>
              </div>
            ) : message.type === "quiz" ? null : message.type ===
              "flashcard" ? (
              <div className={styles.assistantContent}>
                {QuizIntegration.renderFlashcardMessage(
                  message.quizData,
                  onQuizComplete,
                )}
              </div>
            ) : message.type === "quizSuggestion" ? (
              <div className={styles.assistantContent}>
                <p>{message.content}</p>
                <div className={styles.quizSuggestionButtons}>
                  {message.content.toLowerCase().includes("flashcard") ? (
                    <>
                      <button
                        className={`${styles.quizButton} ${styles.flashcardYesButton}`}
                        onClick={() => {
                          // Generate flashcards when user clicks Yes
                          const flashcardData =
                            QuizIntegration.generateFlashcardResponse(
                              `flashcards about ${message.topic}`,
                            );
                          onQuizComplete({
                            type: "startFlashcards",
                            quizData: flashcardData,
                          });
                        }}
                      >
                        Yes, show flashcards
                      </button>
                      <button
                        className={`${styles.quizButton} ${styles.flashcardNoButton}`}
                        onClick={() =>
                          onQuizComplete({ type: "skipFlashcards" })
                        }
                      >
                        No, thanks
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className={`${styles.quizButton} ${styles.quizYesButton}`}
                        onClick={() => {
                          // Generate quiz when user clicks Yes
                          const quizData = QuizIntegration.generateQuizResponse(
                            `quiz about ${message.topic}`,
                          );
                          onQuizComplete({ type: "startQuiz", quizData });
                        }}
                      >
                        Yes, start quiz
                      </button>
                      <button
                        className={`${styles.quizButton} ${styles.quizNoButton}`}
                        onClick={() => onQuizComplete({ type: "skipQuiz" })}
                      >
                        No, thanks
                      </button>
                    </>
                  )}
                </div>
              </div>
            ) : message.meta?.format === "markdown" || !message.meta?.format ? (
              (() => {
                // Helper: find first balanced JSON slice (object or array), ignoring braces inside strings
                const balancedJsonSlice = (text) => {
                  if (!text) return null;
                  const start = text.search(/[{\[]/);
                  if (start === -1) return null;
                  let stack = [];
                  let inStr = false;
                  let esc = false;
                  const open = text[start];
                  const close = open === "{" ? "}" : "]";
                  stack.push(close);
                  for (let i = start + 1; i < text.length; i++) {
                    const ch = text[i];
                    if (inStr) {
                      if (esc) {
                        esc = false;
                      } else if (ch === "\\") {
                        esc = true;
                      } else if (ch === '"') {
                        inStr = false;
                      }
                      continue;
                    }
                    if (ch === '"') {
                      inStr = true;
                      continue;
                    }
                    if (ch === "{") stack.push("}");
                    else if (ch === "[") stack.push("]");
                    else if (ch === stack[stack.length - 1]) stack.pop();
                    if (stack.length === 0) {
                      return text.slice(start, i + 1);
                    }
                  }
                  // Not fully balanced: try to auto-close remaining brackets/braces
                  if (stack.length > 0) {
                    return text.slice(start) + stack.reverse().join("");
                  }
                  return null;
                };
                // Try to detect embedded JSON payloads even when not fenced
                const raw =
                  typeof message.content === "string"
                    ? String(message.content)
                    : "";
                if (raw) {
                  const firstBrace = raw.indexOf("{");
                  const firstBracket = raw.indexOf("[");
                  const start = [firstBrace, firstBracket]
                    .filter((i) => i >= 0)
                    .sort((a, b) => a - b)[0];
                  if (start !== undefined) {
                    const balanced = balancedJsonSlice(raw);
                    const candidate = (balanced || raw.slice(start)).trim();
                    try {
                      const data = JSON.parse(candidate);
                      if (
                        Array.isArray(data) ||
                        (data && Array.isArray(data.cards))
                      ) {
                        const cards = Array.isArray(data) ? data : data.cards;
                        const title = data.title || "Flashcards";
                        if (cards && Array.isArray(cards))
                          return <FlashcardDeck cards={cards} title={title} />;
                      }
                      if (data && Array.isArray(data.questions)) {
                        // Quiz in chat — hide raw JSON; user opens via "Start Quiz" card above
                        return null;
                      }
                    } catch {
                      const slice = balancedJsonSlice(raw);
                      const salvaged = slice
                        ? (() => {
                            try {
                              return JSON.parse(slice);
                            } catch {
                              return null;
                            }
                          })()
                        : null;
                      if (salvaged) {
                        if (
                          Array.isArray(salvaged) ||
                          (salvaged && Array.isArray(salvaged.cards))
                        ) {
                          const cards = Array.isArray(salvaged)
                            ? salvaged
                            : salvaged.cards;
                          const title = salvaged.title || "Flashcards";
                          if (cards && Array.isArray(cards))
                            return (
                              <FlashcardDeck cards={cards} title={title} />
                            );
                        }
                        if (salvaged && Array.isArray(salvaged.questions)) {
                          // Suppress inline rendering in chat
                          return null;
                        }
                      }
                      // Partial parsing fallback: extract as many complete questions as possible
                      const buildPartialTest = (text) => {
                        const getTitle = () => {
                          const m = text.match(/"title"\s*:\s*"([^"]+)"/);
                          return m ? m[1] : "Practice Test";
                        };
                        const qKey = text.search(/"questions"\s*:/);
                        if (qKey === -1) return null;
                        const rest = text.slice(qKey);
                        const firstBracket = rest.indexOf("[");
                        if (firstBracket === -1) return null;
                        const arrSlice = balancedJsonSlice(
                          rest.slice(firstBracket),
                        );
                        const arrText = arrSlice || rest.slice(firstBracket);
                        // Try full array parse first
                        try {
                          const arr = JSON.parse(arrText);
                          if (Array.isArray(arr) && arr.length) {
                            return {
                              title: getTitle(),
                              questions: arr,
                              _incomplete: !arrSlice,
                            };
                          }
                        } catch {}
                        // Fallback: accumulate valid question objects one by one
                        const questions = [];
                        let i = 0;
                        while (i < arrText.length) {
                          const brace = arrText.indexOf("{", i);
                          if (brace === -1) break;
                          const objSlice = balancedJsonSlice(
                            arrText.slice(brace),
                          );
                          if (!objSlice) break;
                          try {
                            const q = JSON.parse(objSlice);
                            if (
                              q &&
                              q.prompt &&
                              Array.isArray(q.choices) &&
                              Array.isArray(q.correctAnswers)
                            ) {
                              questions.push(q);
                            }
                          } catch {}
                          i = brace + objSlice.length;
                        }
                        if (questions.length)
                          return {
                            title: getTitle(),
                            questions,
                            _incomplete: true,
                          };
                        return null;
                      };
                      const partial = buildPartialTest(raw);
                      if (partial) return <TestMode test={partial} />;
                    }
                  }
                }
                // Fallback to enhanced markdown rendering
                return (
                  <ChatMarkdown
                    content={message.content}
                    isStreaming={message.isStreaming || false}
                    className={styles.assistantContent}
                    animation={{
                      enabled: false, // Disable internal animation - we handle streaming at message level
                      speed: 30,
                      tokenize: "word",
                    }}
                  />
                );
              })()
            ) : typeof message.content === "string" ? (
              (() => {
                // Try to parse embedded JSON even for non-markdown messages
                const raw = String(message.content || "");
                // Helper: find first balanced JSON slice and auto-close if truncated
                const balancedJsonSlice = (text) => {
                  if (!text) return null;
                  const start = text.search(/[{}\[]/);
                  if (start === -1) return null;
                  let stack = [];
                  let inStr = false;
                  let esc = false;
                  const open = text[start];
                  const close = open === "{" ? "}" : "]";
                  stack.push(close);
                  for (let i = start + 1; i < text.length; i++) {
                    const ch = text[i];
                    if (inStr) {
                      if (esc) {
                        esc = false;
                      } else if (ch === "\\") {
                        esc = true;
                      } else if (ch === '"') {
                        inStr = false;
                      }
                      continue;
                    }
                    if (ch === '"') {
                      inStr = true;
                      continue;
                    }
                    if (ch === "{") stack.push("}");
                    else if (ch === "[") stack.push("]");
                    else if (ch === stack[stack.length - 1]) stack.pop();
                    if (stack.length === 0) {
                      return text.slice(start, i + 1);
                    }
                  }
                  if (stack.length > 0) {
                    return text.slice(start) + stack.reverse().join("");
                  }
                  return null;
                };
                const salvageParse = (text) => {
                  if (!text) return null;
                  const start = text.search(/[{}\[]/);
                  if (start === -1) return null;
                  let end = text.lastIndexOf("}");
                  let altEnd = text.lastIndexOf("]");
                  let candidateEnd = Math.max(end, altEnd);
                  let attempts = 0;
                  while (candidateEnd > start && attempts < 20) {
                    const slice = text.slice(start, candidateEnd + 1).trim();
                    try {
                      return JSON.parse(slice);
                    } catch {
                      candidateEnd = text.lastIndexOf("}", candidateEnd - 1);
                      if (altEnd > candidateEnd)
                        candidateEnd = altEnd = text.lastIndexOf(
                          "]",
                          candidateEnd - 1,
                        );
                      attempts++;
                    }
                  }
                  return null;
                };
                const firstBrace = raw.indexOf("{");
                const firstBracket = raw.indexOf("[");
                const start = [firstBrace, firstBracket]
                  .filter((i) => i >= 0)
                  .sort((a, b) => a - b)[0];
                if (start !== undefined) {
                  const candidate = raw.slice(start).trim();
                  try {
                    const data = JSON.parse(candidate);
                    if (
                      Array.isArray(data) ||
                      (data && Array.isArray(data.cards))
                    ) {
                      const cards = Array.isArray(data) ? data : data.cards;
                      const title = data.title || "Flashcards";
                      if (cards && Array.isArray(cards))
                        return <FlashcardDeck cards={cards} title={title} />;
                    }
                    if (data && Array.isArray(data.questions)) {
                      const first = data.questions[0] || {};
                      if (Array.isArray(first.correctAnswers))
                        return <TestMode test={data} />;
                      if (Array.isArray(first.answers))
                        return <QuizDeck quiz={data} />;
                    }
                  } catch {
                    const salvaged = salvageParse(raw);
                    if (salvaged) {
                      if (
                        Array.isArray(salvaged) ||
                        (salvaged && Array.isArray(salvaged.cards))
                      ) {
                        const cards = Array.isArray(salvaged)
                          ? salvaged
                          : salvaged.cards;
                        const title = salvaged.title || "Flashcards";
                        if (cards && Array.isArray(cards))
                          return <FlashcardDeck cards={cards} title={title} />;
                      }
                      if (salvaged && Array.isArray(salvaged.questions)) {
                        const first = salvaged.questions[0] || {};
                        if (Array.isArray(first.correctAnswers))
                          return <TestMode test={salvaged} />;
                        if (Array.isArray(first.answers))
                          return <QuizDeck quiz={salvaged} />;
                      }
                    } else {
                      // Partial parsing fallback for plain text
                      const buildPartialTest = (text) => {
                        const getTitle = () => {
                          const m = text.match(/"title"\s*:\s*"([^"]+)"/);
                          return m ? m[1] : "Practice Test";
                        };
                        const qKey = text.search(/"questions"\s*:/);
                        if (qKey === -1) return null;
                        const rest = text.slice(qKey);
                        const firstBracket = rest.indexOf("[");
                        if (firstBracket === -1) return null;
                        const arrSlice = balancedJsonSlice(
                          rest.slice(firstBracket),
                        );
                        const arrText = arrSlice || rest.slice(firstBracket);
                        try {
                          const arr = JSON.parse(arrText);
                          if (Array.isArray(arr) && arr.length) {
                            return {
                              title: getTitle(),
                              questions: arr,
                              _incomplete: !arrSlice,
                            };
                          }
                        } catch {}
                        const questions = [];
                        let i = 0;
                        while (i < arrText.length) {
                          const brace = arrText.indexOf("{", i);
                          if (brace === -1) break;
                          const objSlice = balancedJsonSlice(
                            arrText.slice(brace),
                          );
                          if (!objSlice) break;
                          try {
                            const q = JSON.parse(objSlice);
                            if (
                              q &&
                              q.prompt &&
                              Array.isArray(q.choices) &&
                              Array.isArray(q.correctAnswers)
                            ) {
                              questions.push(q);
                            }
                          } catch {}
                          i = brace + objSlice.length;
                        }
                        if (questions.length)
                          return {
                            title: getTitle(),
                            questions,
                            _incomplete: true,
                          };
                        return null;
                      };
                      const partial = buildPartialTest(raw);
                      if (partial) return <TestMode test={partial} />;
                    }
                  }
                }
                return (
                  <div
                    className={styles.assistantContent}
                    dangerouslySetInnerHTML={{
                      __html: enhanceContentWithVisualizations(message.content),
                    }}
                  />
                );
              })()
            ) : (
              <div className={styles.assistantContent}>{message.content}</div>
            )}
          </motion.div>
          <div className={styles.messageActions}>
            {showCopied && (
              <span className={styles.copiedMessage}>Copied!</span>
            )}
            <Copy
              className={`${styles.messageActionIcon} ${iconStyles.iconHoverGrow}`}
              onClick={handleCopy}
              title="Copy"
              data-label="Copy"
            />
            {onInsertToNote && message.type === "assistant" && (
              <button
                className={styles.insertToNoteBtn}
                onClick={() =>
                  onInsertToNote(
                    typeof message.content === "string"
                      ? message.content
                      : JSON.stringify(message.content),
                    "AI Response",
                  )
                }
                title="Insert into active note"
                aria-label="Insert this response into your active notebook page"
              >
                <BookOpen size={13} />
                Insert to Note
              </button>
            )}
            {onContinue && (
              <button
                className={styles.continueButton}
                onClick={() => onContinue(message)}
                title="Continue response"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Suggested prompts
const suggestions = [
  "Summarize my uploaded document",
  "Create flashcards from my notes",
  "Quiz me on what I've been studying",
  "Help me outline and write my essay",
];

const START_CARDS = [
  {
    icon: UploadCloud,
    color: "#818cf8",
    glow: "rgba(129,140,248,0.15)",
    title: "Study from a document",
    desc: "Upload a PDF — get summaries, quizzes, and explanations instantly",
    prompt: "Help me study from a document I'll upload",
  },
  {
    icon: PenLine,
    color: "#f97316",
    glow: "rgba(249,115,22,0.15)",
    title: "Write an assignment",
    desc: "AI drafts, structures, and polishes your essay or paper in minutes",
    prompt: null,
    action: "assignment",
  },
  {
    icon: BrainCircuit,
    color: "#34d399",
    glow: "rgba(52,211,153,0.15)",
    title: "Quiz yourself",
    desc: "Turn any topic or doc into practice questions right now",
    prompt: "Create a quiz to help me study",
  },
];

// Empty state component with centered input layout
const EmptyState = ({
  onPromptClick,
  children,
  quickActions,
  onAssignmentOpen,
}) => {
  const { user } = useAuth();
  const hour = new Date().getHours();
  const greeting =
    hour < 5
      ? "Still up?"
      : hour < 12
        ? "Good morning"
        : hour < 17
          ? "Good afternoon"
          : "Good evening";
  const firstName =
    user?.user_metadata?.full_name?.split(" ")[0] ||
    user?.user_metadata?.name?.split(" ")[0] ||
    user?.email?.split("@")[0] ||
    "there";

  return (
    <div className={styles.emptyState}>
      {/* Greeting block */}
      <div className={styles.emptyGreetBlock}>
        <div className={styles.aiIcon}>
          <img src={aiStarLogo} alt="Lumi" width={52} height={52} />
        </div>
        <h1 className={styles.chatHeading}>
          <span className={styles.gradientText}>
            {greeting}, {firstName}.
          </span>
        </h1>
        <p className={styles.workspaceTagline}>What are we studying today?</p>
      </div>

      {/* Start cards */}
      <div className={styles.startCards}>
        {START_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.title}
              className={styles.startCard}
              style={{ "--card-accent": card.color, "--card-glow": card.glow }}
              onClick={() =>
                card.action === "assignment"
                  ? onAssignmentOpen?.()
                  : onPromptClick?.(card.prompt)
              }
            >
              <span className={styles.startCardIcon}>
                <Icon size={18} strokeWidth={1.8} />
              </span>
              <div className={styles.startCardBody}>
                <span className={styles.startCardTitle}>{card.title}</span>
                <span className={styles.startCardDesc}>{card.desc}</span>
              </div>
              <span className={styles.startCardArrow}>›</span>
            </button>
          );
        })}
      </div>

      {/* Input box passed as children */}
      {children}

      {/* Suggestion chips below input */}
      <div className={styles.emptyStateSuggestions}>
        {suggestions.map((prompt, index) => (
          <button
            key={index}
            className={styles.emptyStatePromptButton}
            onClick={() => onPromptClick && onPromptClick(prompt)}
          >
            {prompt}
          </button>
        ))}
      </div>

      {/* Quick actions at bottom */}
      {quickActions}
    </div>
  );
};

const HomePage = ({
  shellEmbedded = false,
  pendingMessage = null,
  onPendingMessageConsumed = null,
  pendingChatAppend = null,
  onPendingChatAppendConsumed = null,
  getExtraContext = null,
}) => {
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const { isUserPremium, user, signOut } = useAuth();
  const { openComponentTab, isCollapsed } = useSidePanel();
  const { saveGeneratedContent } = useGeneratedContent();
  const { activeNote, insertIntoActiveNote, getActiveNoteContext } =
    useActiveNote();
  const { layout } = useLayout();
  const {
    isCanvasOpen,
    activeWorkspace,
    closeCanvas,
    createWorkspace,
    openWorkspace,
    workspaces: savedWorkspaces,
  } = useWorkspace();
  const [activeModal, setActiveModal] = useState(null);
  // Global sidebar state that persists across component re-mounts
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      // Check if there's a global state first (for immediate re-mounts)
      if (window.globalSidebarState !== undefined) {
        return window.globalSidebarState;
      }

      // Otherwise use localStorage
      const saved = localStorage.getItem("sidebarOpen");
      const initialState = saved !== null ? JSON.parse(saved) : true;

      // Set global state for future re-mounts
      window.globalSidebarState = initialState;
      return initialState;
    } catch (error) {
      window.globalSidebarState = true;
      return true;
    }
  });
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  // Assignment Group state
  const { createGroup, setActiveGroup, setAssignmentDoc, addReferenceDoc } =
    useAssignment();
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showAssignmentBuilder, setShowAssignmentBuilder] = useState(false);
  const [showTest, setShowTest] = useState(false);
  const [showDocumentLibrary, setShowDocumentLibrary] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  // Study Tool Source Selection Modal
  const [showStudyToolModal, setShowStudyToolModal] = useState(false);
  const [selectedStudyTool, setSelectedStudyTool] = useState(null); // 'quiz', 'flashcards', 'notes'

  // Layout Selector
  const [showLayoutSelector, setShowLayoutSelector] = useState(false);
  const layoutBtnRef = useRef(null);

  // Content Side Panel (replaces immersive view and quotes viewer)
  const [showContentPanel, setShowContentPanel] = useState(false);
  const [contentPanelTabs, setContentPanelTabs] = useState([]);
  const contentPanelTabsRef = useRef([]);
  const [activeContentTab, setActiveContentTab] = useState(0);
  const [isContentPanelMinimized, setIsContentPanelMinimized] = useState(false);

  // Document Context for Chat
  const [attachedDocuments, setAttachedDocuments] = useState([]);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [showDocumentPicker, setShowDocumentPicker] = useState(false);
  const [showDocContextDropdown, setShowDocContextDropdown] = useState(false);
  const docContextDropdownRef = useRef(null);

  // Document Insights — auto-generated summary shown when a doc is uploaded
  const [docInsights, setDocInsights] = useState(null); // { docName, loading, data }
  const fetchedInsightsRef = useRef(new Set());

  // Handwriting mode for better retention
  const [handwritingMode, setHandwritingMode] = useState(() => {
    try {
      const saved = localStorage.getItem("handwritingMode");
      return saved === "true";
    } catch {
      return false;
    }
  });

  // Unified view mode - 'chat' or 'notes' - persists across sessions
  const [viewMode, setViewMode] = useState(() => {
    try {
      return localStorage.getItem("viewMode") || "chat";
    } catch {
      return "chat";
    }
  });

  const { content: notebookSidebarContent } = useNotebookSidebar();

  const switchViewMode = useCallback((mode) => {
    setViewMode(mode);
    try {
      localStorage.setItem("viewMode", mode);
    } catch {}
  }, []);

  // Apply handwriting mode to document body
  useEffect(() => {
    if (handwritingMode) {
      document.body.classList.add("handwriting-mode");
    } else {
      document.body.classList.remove("handwriting-mode");
    }
    localStorage.setItem("handwritingMode", handwritingMode.toString());
  }, [handwritingMode]);

  // Close document context dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        docContextDropdownRef.current &&
        !docContextDropdownRef.current.contains(e.target)
      ) {
        if (showDocContextDropdown) {
          setIsClosing(true);
          setTimeout(() => {
            setShowDocContextDropdown(false);
            setIsClosing(false);
          }, 150);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDocContextDropdown]);

  // Keep ref in sync so addContentTab can read current length without a stale closure
  useEffect(() => {
    contentPanelTabsRef.current = contentPanelTabs;
  }, [contentPanelTabs]);

  // Add content to side panel
  const addContentTab = useCallback((type, content, title, documentName) => {
    const newTab = {
      type,
      content,
      title,
      documentName,
      id: `tab_${Date.now()}_${Math.random().toString(36).slice(2)}`,
    };
    // Read current length via ref to avoid stale closure — never call setState inside another setState updater
    const newIndex = contentPanelTabsRef.current.length;
    setContentPanelTabs((prev) => [...prev, newTab]);
    setActiveContentTab(newIndex);
    setShowContentPanel(true);
    setIsContentPanelMinimized(false);
  }, []);

  // Callback for immersive view (legacy compatibility)
  const handleOpenImmersive = useCallback(
    (data) => {
      addContentTab(
        data.contentType,
        data.content,
        data.document?.name || "Content",
        data.document?.name,
      );
    },
    [addContentTab],
  );

  // Handle OAuth callback (Google Sign-In redirects here with auth params)
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Check if this is an OAuth callback (has code or access_token in URL)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const searchParams = new URLSearchParams(window.location.search);

      if (hashParams.get("access_token") || searchParams.get("code")) {
        console.log("🔐 Processing OAuth callback on /home");

        // Session is automatically handled by Supabase client
        // Just clean up the URL to remove auth parameters
        window.history.replaceState({}, document.title, "/home");
      }
    };

    handleOAuthCallback();
  }, []);

  useEffect(() => {
    // Load documents for modal selections
    setAvailableDocuments(getFilesFromStorage());
    const onFilesUpdated = () => setAvailableDocuments(getFilesFromStorage());
    window.addEventListener("filesUpdated", onFilesUpdated);
    return () => window.removeEventListener("filesUpdated", onFilesUpdated);
  }, []);

  // Command Palette keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setShowCommandPalette(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Handle command palette actions
  const handleCommand = (commandId) => {
    switch (commandId) {
      case "quiz":
        handlePromptClick("Generate a 10 question quiz");
        break;
      case "flashcards":
        handlePromptClick("Generate flashcards");
        break;
      case "test":
        setShowTest(true);
        break;
      case "notes":
        handlePromptClick("Generate study notes");
        break;
      case "upload":
        setShowDocumentLibrary(true);
        break;
      case "timer":
        // TODO: Implement study timer
        console.log("Study timer coming soon!");
        break;
      case "search":
        setSearchOpen(true);
        break;
      case "progress":
        // TODO: Implement progress dashboard
        console.log("Progress dashboard coming soon!");
        break;
      case "settings":
        // TODO: Implement settings
        console.log("Settings coming soon!");
        break;
      default:
        break;
    }
  };

  // Get user-specific prefix for localStorage keys
  const getUserPrefix = useCallback(() => {
    return user?.id ? `user_${user.id}_` : "anonymous_";
  }, [user]);

  // Helper functions for user-scoped localStorage
  const getLocalStorageItem = useCallback(
    (key) => {
      return localStorage.getItem(`${getUserPrefix()}${key}`);
    },
    [getUserPrefix],
  );

  const setLocalStorageItem = useCallback(
    (key, value) => {
      localStorage.setItem(`${getUserPrefix()}${key}`, value);
    },
    [getUserPrefix],
  );

  const removeLocalStorageItem = useCallback(
    (key) => {
      localStorage.removeItem(`${getUserPrefix()}${key}`);
    },
    [getUserPrefix],
  );

  // Now we can use the localStorage helpers safely
  const [messages, setMessages] = useState(() => {
    // Try to load messages for active chat from localStorage with user prefix
    const savedActiveChat = getLocalStorageItem("activeChat");
    if (savedActiveChat) {
      const savedMessages = getLocalStorageItem(
        `chatMessages_${savedActiveChat}`,
      );
      return savedMessages ? JSON.parse(savedMessages) : [];
    }
    return [];
  });
  const [input, setInput] = useState("");
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [thinkingMessage, setThinkingMessage] = useState("Thinking...");
  const [isTabSwitching, setIsTabSwitching] = useState(false);
  const [streamingIntervals, setStreamingIntervals] = useState(new Set());
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamId, setCurrentStreamId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Removed Assignment quick action button; modal can still be opened via other UI if needed
  // Store quiz results for potential future features like history, analytics, or resuming quizzes
  // Currently set in handleQuizComplete but not actively used in the UI
  // eslint-disable-next-line no-unused-vars
  const [quizResults, setQuizResults] = useState({});
  const [chats, setChats] = useState(() => {
    // Try to load chats from localStorage with user prefix
    const savedChats = getLocalStorageItem("chats");
    return savedChats ? JSON.parse(savedChats) : [];
  });

  // Scroll to bottom when messages change or thinking state changes
  useEffect(() => {
    if (chatContainerRef.current && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      setTimeout(() => {
        const container = chatContainerRef.current;
        if (!container) return; // Guard against null ref

        if (lastMessage.type === "user") {
          // For user messages: scroll to show the message with some space above and below
          const userMessages = container.querySelectorAll(
            '[data-message-type="user"]',
          );
          const lastUserMessage = userMessages[userMessages.length - 1];

          if (lastUserMessage) {
            const messageTop = lastUserMessage.offsetTop;
            const containerHeight = container.clientHeight;

            // Position user message about 1/4 from the top of the viewport
            const targetScroll = messageTop - containerHeight * 0.25;

            container.scrollTo({
              top: Math.max(0, targetScroll),
              behavior: "smooth",
            });
          }
        } else {
          // For AI responses: scroll to bottom to show complete response
          container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
          });
        }
      }, 100);
    }
  }, [messages, isThinking]);

  const [activeChat, setActiveChat] = useState(() => {
    // Try to load active chat from localStorage with user prefix
    return getLocalStorageItem("activeChat") || null;
  });

  // State for editing chat titles
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  // Options menu and ref removed
  const { isDarkMode, toggleTheme } = useTheme();
  const chatAreaRef = useRef(null);
  const chatEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [selectedFormat, setSelectedFormat] = useState("");
  const [showFormatDropdown, setShowFormatDropdown] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showTestParams, setShowTestParams] = useState(false);
  // Notes are now opened as a tab in the side panel via openComponentPanel
  const formatDropdownRef = useRef(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        formatDropdownRef.current &&
        !formatDropdownRef.current.contains(event.target)
      ) {
        if (showFormatDropdown) {
          setIsClosing(true);
          setTimeout(() => {
            setShowFormatDropdown(false);
            setIsClosing(false);
          }, 150);
        }
      }
    };

    if (showFormatDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFormatDropdown]);

  // Keyboard: Cmd/Ctrl+J opens Notes tab in side panel as a tab
  useEffect(() => {
    const handler = (e) => {
      const isToggle = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j";
      if (isToggle) {
        e.preventDefault();
        if (openComponentTab) {
          openComponentTab(<NotesTab chatId={activeChat} />, "Notes");
        }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [activeChat, openComponentTab]);

  // Helper function to extract text from document (used by study actions and chat context)
  const extractDocumentText = useCallback(async (doc) => {
    // If document already has textContent (DOC/DOCX files), use it
    if (doc.textContent && doc.textContent.trim().length > 100) {
      console.log("Using pre-extracted textContent for:", doc.name);
      return doc.textContent;
    }

    // For PDFs and other files, need to process via backend
    const isPdf =
      doc.type === "application/pdf" ||
      doc.name?.toLowerCase().endsWith(".pdf");

    if (isPdf) {
      console.log("Processing PDF via backend:", doc.name);
      // Convert base64 to blob and upload to process_document endpoint
      const byteCharacters = atob(doc.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: doc.type });
      const file = new File([blob], doc.name, { type: doc.type });

      const formData = new FormData();
      formData.append("file", file);

      const processResponse = await fetch(`${API_BASE}/api/process_document`, {
        method: "POST",
        body: formData,
      });

      if (!processResponse.ok) {
        const errorData = await processResponse.json().catch(() => ({}));
        throw new Error(errorData.error || "Failed to process document");
      }

      const processData = await processResponse.json();
      console.log("PDF processing result:", {
        pages: processData.pages?.length,
        textLength: processData.text?.length,
        preview: processData.text?.substring(0, 200),
      });

      // Extract text from pages or use full text
      let extractedText = "";
      if (processData.pages && Array.isArray(processData.pages)) {
        extractedText = processData.pages.map((p) => p.text).join("\n\n");
      } else if (processData.text) {
        extractedText = processData.text;
      }

      if (!extractedText || extractedText.trim().length < 100) {
        throw new Error(
          "Document contains very little readable text. It may be a scanned PDF without OCR.",
        );
      }

      console.log(`Extracted ${extractedText.length} characters from PDF`);
      return extractedText;
    }

    // For text files, try to decode base64
    try {
      return atob(doc.content);
    } catch {
      return doc.content;
    }
  }, []);

  // Handle study action requests from ResourcesSidebar
  useEffect(() => {
    // Helper to get File object from doc (handles both file and base64 content)
    const getFileFromDoc = (document) => {
      if (document.file) {
        console.log("🔧 [FILE HELPER] Using existing file object");
        return document.file;
      }
      if (document.content && document.type) {
        console.log("🔧 [FILE HELPER] Converting base64 to File...");
        try {
          const byteCharacters = atob(document.content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const file = new File([byteArray], document.name, {
            type: document.type,
          });
          console.log(
            "🔧 [FILE HELPER] Created file:",
            file.name,
            (file.size / 1024).toFixed(1) + "KB",
          );
          return file;
        } catch (e) {
          console.warn("🔧 [FILE HELPER] Failed to convert base64:", e);
          return null;
        }
      }
      console.warn("🔧 [FILE HELPER] No file or content available");
      return null;
    };

    const handleStudyContent = async (event) => {
      const { action, document: doc } = event.detail;
      const actionId = typeof action === "string" ? action : action?.id;

      if (actionId === "notes") {
        // Generate study notes - show loading state first
        openComponentTab(
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "var(--foreground-muted)",
            }}
          >
            <div style={{ marginBottom: "16px", fontSize: "24px" }}>📝</div>
            <div>Processing {doc.name}...</div>
            <div style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}>
              {USE_ENHANCED_PARSER
                ? "🚀 Using ENHANCED parser (extracts sections, tables, definitions)"
                : "Using basic parser"}
            </div>
          </div>,
          `Notes: ${doc.name}`,
        );
        try {
          let notesContent = null;
          let usedEnhanced = false;

          // Try enhanced parser first if enabled
          const docFile = USE_ENHANCED_PARSER ? getFileFromDoc(doc) : null;

          if (USE_ENHANCED_PARSER && docFile) {
            console.log(
              "📚 [ENHANCED PARSER] Starting enhanced notes generation for:",
              doc.name,
            );
            console.log(
              "📚 [ENHANCED PARSER] Using endpoint:",
              LOCAL_API_URLS.ENHANCED_GENERATE,
            );
            console.log(
              "📚 [ENHANCED PARSER] File size:",
              (docFile.size / 1024).toFixed(1) + "KB",
            );

            openComponentTab(
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "var(--foreground-muted)",
                }}
              >
                <div style={{ marginBottom: "16px", fontSize: "24px" }}>📝</div>
                <div>Generating study notes with ENHANCED parser...</div>
                <div
                  style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}
                >
                  🚀 Extracting sections, tables, definitions, key quotes...
                </div>
              </div>,
              `Notes: ${doc.name}`,
            );

            try {
              const formData = new FormData();
              formData.append("document", docFile);
              formData.append("type", "notes");

              console.log(
                "📚 [ENHANCED PARSER] Sending file to /api/enhanced_generate...",
              );
              const startTime = Date.now();

              const response = await fetch(LOCAL_API_URLS.ENHANCED_GENERATE, {
                method: "POST",
                body: formData,
              });

              const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
              console.log(
                `📚 [ENHANCED PARSER] Response received in ${elapsed}s, status: ${response.status}`,
              );

              if (response.ok) {
                const data = await response.json();
                console.log(
                  "📚 [ENHANCED PARSER] Success! Metadata:",
                  data.metadata,
                );
                console.log(
                  "📚 [ENHANCED PARSER] Sections used:",
                  data.metadata?.sections_used,
                );
                console.log(
                  "📚 [ENHANCED PARSER] Definitions found:",
                  data.metadata?.definitions_found,
                );
                console.log(
                  "📚 [ENHANCED PARSER] Key quotes found:",
                  data.metadata?.key_quotes_found,
                );

                if (data.result?.notes) {
                  notesContent = data.result.notes;
                  usedEnhanced = true;
                  console.log(
                    "📚 [ENHANCED PARSER] Notes generated successfully!",
                  );
                }
              } else {
                console.warn(
                  "📚 [ENHANCED PARSER] Failed, falling back to basic parser",
                );
              }
            } catch (enhancedError) {
              console.warn(
                "📚 [ENHANCED PARSER] Error:",
                enhancedError.message,
                "- Falling back to basic parser",
              );
            }
          }

          // Fallback to basic parser if enhanced didn't work
          if (!notesContent) {
            console.log(
              "📄 [BASIC PARSER] Using basic text extraction for notes",
            );

            const extractedText = await extractDocumentText(doc);

            if (!extractedText || extractedText.trim().length < 50) {
              throw new Error("Could not extract enough text from document");
            }

            openComponentTab(
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "var(--foreground-muted)",
                }}
              >
                <div style={{ marginBottom: "16px", fontSize: "24px" }}>📝</div>
                <div>Generating study notes...</div>
                <div
                  style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}
                >
                  Using basic parser (15-30 seconds)
                </div>
              </div>,
              `Notes: ${doc.name}`,
            );

            const MAX_TEXT_LENGTH = 30000;
            let textToSend = extractedText;

            if (extractedText.length > MAX_TEXT_LENGTH) {
              console.log(
                `📄 [BASIC PARSER] Document too large (${extractedText.length} chars), truncating to ${MAX_TEXT_LENGTH}`,
              );
              textToSend = extractedText.substring(0, MAX_TEXT_LENGTH);
            }

            console.log(
              `📄 [BASIC PARSER] Sending ${textToSend.length} chars to /api/generate for notes`,
            );

            const response = await fetch(LOCAL_API_URLS.GENERATE, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                type: "notes",
                text: textToSend,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(
                errorData.error || `Server error: ${response.status}`,
              );
            }

            const data = await response.json();
            console.log("📄 [BASIC PARSER] Notes generation response:", {
              hasResult: !!data.result,
              hasNotes: !!data.notes,
              type: data.type,
            });

            notesContent = data.result || data.notes || data.content;
          }

          console.log(
            `✅ Notes generated using ${
              usedEnhanced ? "ENHANCED" : "BASIC"
            } parser`,
          );

          if (notesContent) {
            // Save to recently generated
            saveGeneratedContent({
              name: `Notes: ${doc.name}`,
              type: "notes",
              documentName: doc.name,
              data: notesContent,
            });

            openComponentTab(
              <StudyNotesView
                content={notesContent}
                documentName={doc.name}
                document={doc}
              />,
              `Notes: ${doc.name}`,
            );
          } else {
            throw new Error("No notes generated - please try again");
          }
        } catch (error) {
          console.error("Error generating notes:", error);
          openComponentTab(
            <div
              style={{ padding: "40px", textAlign: "center", color: "#ef4444" }}
            >
              <div style={{ marginBottom: "16px", fontSize: "24px" }}>❌</div>
              <div>Failed to generate notes: {error.message}</div>
            </div>,
            `Notes: ${doc.name}`,
          );
        }
      } else if (actionId === "quotes") {
        // Generate key quotes - show loading state first
        openComponentTab(
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "var(--foreground-muted)",
            }}
          >
            <div style={{ marginBottom: "16px", fontSize: "24px" }}>💬</div>
            <div>Processing {doc.name}...</div>
            <div style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}>
              {USE_ENHANCED_PARSER
                ? "🚀 Using ENHANCED parser (pre-extracts key quotes)"
                : "Using basic parser"}
            </div>
          </div>,
          `Quotes: ${doc.name}`,
        );
        try {
          let quotesData = null;
          let usedEnhanced = false;

          // Try enhanced parser first if enabled
          const docFile = USE_ENHANCED_PARSER ? getFileFromDoc(doc) : null;

          if (USE_ENHANCED_PARSER && docFile) {
            console.log(
              "💬 [ENHANCED PARSER] Starting enhanced quotes extraction for:",
              doc.name,
            );

            openComponentTab(
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "var(--foreground-muted)",
                }}
              >
                <div style={{ marginBottom: "16px", fontSize: "24px" }}>💬</div>
                <div>Extracting key quotes with ENHANCED parser...</div>
                <div
                  style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}
                >
                  🚀 Finding definitions, key insights, examples...
                </div>
              </div>,
              `Quotes: ${doc.name}`,
            );

            try {
              const formData = new FormData();
              formData.append("document", docFile);
              formData.append("type", "quotes");

              console.log(
                "💬 [ENHANCED PARSER] Sending file to /api/enhanced_generate...",
              );
              const startTime = Date.now();

              const response = await fetch(LOCAL_API_URLS.ENHANCED_GENERATE, {
                method: "POST",
                body: formData,
              });

              const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
              console.log(
                `💬 [ENHANCED PARSER] Response received in ${elapsed}s`,
              );

              if (response.ok) {
                const data = await response.json();
                console.log(
                  "💬 [ENHANCED PARSER] Success! Metadata:",
                  data.metadata,
                );

                if (data.result?.quotes) {
                  quotesData = { quotes: data.result.quotes };
                  usedEnhanced = true;
                  console.log(
                    `💬 [ENHANCED PARSER] Extracted ${data.result.quotes.length} quotes!`,
                  );
                }
              } else {
                console.warn(
                  "💬 [ENHANCED PARSER] Failed, falling back to basic parser",
                );
              }
            } catch (enhancedError) {
              console.warn(
                "💬 [ENHANCED PARSER] Error:",
                enhancedError.message,
              );
            }
          }

          // Fallback to basic parser
          if (!quotesData) {
            console.log("📄 [BASIC PARSER] Using basic extraction for quotes");

            const extractedText = await extractDocumentText(doc);

            if (!extractedText || extractedText.trim().length < 50) {
              throw new Error("Could not extract enough text from document");
            }

            openComponentTab(
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "var(--foreground-muted)",
                }}
              >
                <div style={{ marginBottom: "16px", fontSize: "24px" }}>💬</div>
                <div>Finding key quotes...</div>
                <div
                  style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}
                >
                  Using basic parser (15-30 seconds)
                </div>
              </div>,
              `Quotes: ${doc.name}`,
            );

            const MAX_TEXT_LENGTH = 30000;
            let textToSend =
              extractedText.length > MAX_TEXT_LENGTH
                ? extractedText.substring(0, MAX_TEXT_LENGTH)
                : extractedText;

            console.log(
              `📄 [BASIC PARSER] Sending ${textToSend.length} chars for quotes`,
            );

            const response = await fetch(LOCAL_API_URLS.GENERATE, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                type: "quotes",
                text: textToSend,
              }),
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              throw new Error(
                errorData.error || `Server error: ${response.status}`,
              );
            }

            const data = await response.json();
            console.log("📄 [BASIC PARSER] Quotes generation response:", data);
            quotesData = data.result || data;
          }

          console.log(
            `✅ Quotes extracted using ${
              usedEnhanced ? "ENHANCED" : "BASIC"
            } parser`,
          );

          if (quotesData?.quotes) {
            saveGeneratedContent({
              name: `Quotes: ${doc.name}`,
              type: "quotes",
              documentName: doc.name,
              data: quotesData.quotes,
            });

            openComponentTab(
              <QuotesView
                quotes={quotesData.quotes}
                documentName={doc.name}
                document={doc}
              />,
              `Quotes: ${doc.name}`,
            );
          } else {
            throw new Error("No quotes generated - please try again");
          }
        } catch (error) {
          console.error("Error generating quotes:", error);
          openComponentTab(
            <div
              style={{ padding: "40px", textAlign: "center", color: "#ef4444" }}
            >
              <div style={{ marginBottom: "16px", fontSize: "24px" }}>❌</div>
              <div>Failed to extract quotes: {error.message}</div>
            </div>,
            `Quotes: ${doc.name}`,
          );
        }
      } else if (actionId === "quiz") {
        // Generate quiz - show loading and process document
        openComponentTab(
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "var(--foreground-muted)",
            }}
          >
            <div style={{ marginBottom: "16px", fontSize: "24px" }}>❓</div>
            <div>Processing {doc.name}...</div>
            <div style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}>
              {USE_ENHANCED_PARSER
                ? "🚀 Using ENHANCED parser (12-15 questions)"
                : "Preparing to generate quiz"}
            </div>
          </div>,
          `Quiz: ${doc.name}`,
        );
        try {
          // Try enhanced parser first
          const docFile = USE_ENHANCED_PARSER ? getFileFromDoc(doc) : null;

          if (USE_ENHANCED_PARSER && docFile) {
            console.log(
              "❓ [ENHANCED PARSER] Starting enhanced quiz generation for:",
              doc.name,
            );

            openComponentTab(
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "var(--foreground-muted)",
                }}
              >
                <div style={{ marginBottom: "16px", fontSize: "24px" }}>❓</div>
                <div>Generating quiz with ENHANCED parser...</div>
                <div
                  style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}
                >
                  🚀 Analyzing sections, definitions, key concepts...
                </div>
              </div>,
              `Quiz: ${doc.name}`,
            );

            try {
              const formData = new FormData();
              formData.append("document", docFile);
              formData.append("type", "quiz");

              console.log(
                "❓ [ENHANCED PARSER] Sending file to /api/enhanced_generate...",
              );
              const startTime = Date.now();

              const response = await fetch(LOCAL_API_URLS.ENHANCED_GENERATE, {
                method: "POST",
                body: formData,
              });

              const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
              console.log(
                `❓ [ENHANCED PARSER] Response received in ${elapsed}s`,
              );

              if (response.ok) {
                const data = await response.json();
                console.log(
                  "❓ [ENHANCED PARSER] Success! Metadata:",
                  data.metadata,
                );

                if (data.result?.questions) {
                  console.log(
                    `❓ [ENHANCED PARSER] Generated ${data.result.questions.length} questions!`,
                  );

                  // Add quiz as a chat message so user can click "Start Quiz"
                  const quizMsg = {
                    id: Date.now(),
                    type: "assistant",
                    content: `\`\`\`quiz\n${JSON.stringify(data.result, null, 2)}\n\`\`\``,
                    timestamp: new Date().toISOString(),
                  };
                  setMessages((prev) => [...prev, quizMsg]);
                  return;
                }
              }
              console.warn(
                "❓ [ENHANCED PARSER] Failed to generate quiz, falling back to chat",
              );
            } catch (enhancedError) {
              console.warn(
                "❓ [ENHANCED PARSER] Error:",
                enhancedError.message,
              );
            }
          }

          // Fallback to chat-based quiz generation
          console.log("📄 [BASIC PARSER] Using chat-based quiz generation");
          const extractedText = await extractDocumentText(doc);
          const message = `Create a comprehensive quiz based on this document content:\n\n${extractedText.substring(
            0,
            8000,
          )}`;
          setInput(message);
          setTimeout(() => {
            document
              .getElementById("chatInputBar")
              ?.querySelector("form")
              ?.requestSubmit();
          }, 100);
        } catch (error) {
          console.error("Error processing document for quiz:", error);
        }
      } else if (actionId === "flashcards") {
        // Generate flashcards
        openComponentTab(
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "var(--foreground-muted)",
            }}
          >
            <div style={{ marginBottom: "16px", fontSize: "24px" }}>📚</div>
            <div>Processing {doc.name}...</div>
            <div style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}>
              {USE_ENHANCED_PARSER
                ? "🚀 Using ENHANCED parser (25-40 cards from definitions)"
                : "Preparing to generate flashcards"}
            </div>
          </div>,
          `Flashcards: ${doc.name}`,
        );
        try {
          // Try enhanced parser first
          const docFile = USE_ENHANCED_PARSER ? getFileFromDoc(doc) : null;

          if (USE_ENHANCED_PARSER && docFile) {
            console.log(
              "📚 [ENHANCED PARSER] Starting enhanced flashcard generation for:",
              doc.name,
            );

            openComponentTab(
              <div
                style={{
                  padding: "40px",
                  textAlign: "center",
                  color: "var(--foreground-muted)",
                }}
              >
                <div style={{ marginBottom: "16px", fontSize: "24px" }}>📚</div>
                <div>Generating flashcards with ENHANCED parser...</div>
                <div
                  style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}
                >
                  🚀 Using pre-extracted definitions and key concepts...
                </div>
              </div>,
              `Flashcards: ${doc.name}`,
            );

            try {
              const formData = new FormData();
              formData.append("document", docFile);
              formData.append("type", "flashcards");

              console.log(
                "📚 [ENHANCED PARSER] Sending file to /api/enhanced_generate...",
              );
              const startTime = Date.now();

              const response = await fetch(LOCAL_API_URLS.ENHANCED_GENERATE, {
                method: "POST",
                body: formData,
              });

              const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
              console.log(
                `📚 [ENHANCED PARSER] Response received in ${elapsed}s`,
              );

              if (response.ok) {
                const data = await response.json();
                console.log(
                  "📚 [ENHANCED PARSER] Success! Metadata:",
                  data.metadata,
                );

                if (data.result?.cards) {
                  console.log(
                    `📚 [ENHANCED PARSER] Generated ${data.result.cards.length} flashcards!`,
                  );

                  // Dispatch flashcard event
                  window.dispatchEvent(
                    new CustomEvent("openFlashcardPanel", {
                      detail: {
                        type: "flashcards",
                        data: data.result,
                        title: data.result.title || `Flashcards: ${doc.name}`,
                      },
                    }),
                  );
                  return;
                }
              }
              console.warn(
                "📚 [ENHANCED PARSER] Failed to generate flashcards, falling back to chat",
              );
            } catch (enhancedError) {
              console.warn(
                "📚 [ENHANCED PARSER] Error:",
                enhancedError.message,
              );
            }
          }

          // Fallback to chat-based generation
          console.log(
            "📄 [BASIC PARSER] Using chat-based flashcard generation",
          );
          const extractedText = await extractDocumentText(doc);
          const message = `Create flashcards based on this document content:\n\n${extractedText.substring(
            0,
            8000,
          )}`;
          setInput(message);
          setTimeout(() => {
            document
              .getElementById("chatInputBar")
              ?.querySelector("form")
              ?.requestSubmit();
          }, 100);
        } catch (error) {
          console.error("Error processing document for flashcards:", error);
        }
      } else if (actionId === "test") {
        // Generate test
        openComponentTab(
          <div
            style={{
              padding: "40px",
              textAlign: "center",
              color: "var(--foreground-muted)",
            }}
          >
            <div style={{ marginBottom: "16px", fontSize: "24px" }}>📋</div>
            <div>Processing {doc.name}...</div>
            <div style={{ marginTop: "8px", fontSize: "12px", opacity: 0.7 }}>
              Preparing to generate practice test
            </div>
          </div>,
          `Test: ${doc.name}`,
        );
        try {
          const extractedText = await extractDocumentText(doc);
          const message = `Create a practice test based on this document content:\n\n${extractedText.substring(
            0,
            8000,
          )}`;
          setInput(message);
          setTimeout(() => {
            document
              .getElementById("chatInputBar")
              ?.querySelector("form")
              ?.requestSubmit();
          }, 100);
        } catch (error) {
          console.error("Error processing document for test:", error);
        }
      }
    };

    // Handle viewing quote in document with highlighting
    const handleViewQuoteInDocument = async (event) => {
      const { quote, document: doc } = event.detail;

      if (!doc || !quote) {
        console.error("Missing document or quote for view in document");
        return;
      }

      try {
        // Convert base64 to File if needed
        let file = doc.file || doc;
        if (doc.content && doc.type && !doc.file) {
          // Convert base64 to File
          const byteCharacters = atob(doc.content);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          file = new File([byteArray], doc.name, { type: doc.type });
        }

        // Dispatch event to open the document in side panel with quote to highlight
        window.dispatchEvent(
          new CustomEvent("openDocumentWithHighlight", {
            detail: {
              file: file,
              quote: quote,
              searchText: quote.quote,
            },
          }),
        );
      } catch (error) {
        console.error("Error opening document with highlights:", error);
      }
    };

    window.addEventListener("generateStudyContent", handleStudyContent);
    window.addEventListener("viewQuoteInDocument", handleViewQuoteInDocument);
    return () => {
      window.removeEventListener("generateStudyContent", handleStudyContent);
      window.removeEventListener(
        "viewQuoteInDocument",
        handleViewQuoteInDocument,
      );
    };
  }, [openComponentTab, setInput, saveGeneratedContent]);

  // Persist activeChat for cross-component access (e.g., Notes opened from Resources)
  useEffect(() => {
    try {
      if (activeChat) {
        localStorage.setItem("activeChatId", String(activeChat));
      }
    } catch {}
  }, [activeChat]);

  // Trigger clip-path animation on initial load
  useEffect(() => {
    const sidebar = document.querySelector(`.${styles.sidebar}`);
    if (sidebar && sidebarOpen) {
      sidebar.style.clipPath = "inset(0 100% 0 0)";
      sidebar.style.transition = "clip-path 0.8s cubic-bezier(0.4, 0, 0.2, 1)";

      // Force reflow
      void sidebar.offsetHeight;

      // Trigger animation
      requestAnimationFrame(() => {
        sidebar.style.clipPath = "inset(0 0 0 0)";
      });

      // Clean up after animation
      setTimeout(() => {
        sidebar.style.clipPath = "";
        sidebar.style.transition = "";
      }, 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // Handle click outside to close search
  useEffect(() => {
    const handleClickOutsideSearch = (event) => {
      const searchContainer = document.querySelector(
        `.${styles.searchContainer}`,
      );
      if (
        searchContainer &&
        !searchContainer.contains(event.target) &&
        searchOpen
      ) {
        setSearchOpen(false);
        setSearchQuery("");
      }
    };

    if (searchOpen) {
      document.addEventListener("mousedown", handleClickOutsideSearch);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutsideSearch);
    };
  }, [searchOpen]);

  // Auto-focus search input when search opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Auto-focus search input when search opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  // Toggle sidebar function used by sidebar buttons
  const toggleSidebar = useCallback(() => {
    setSidebarOpen((prev) => {
      // If we're closing the sidebar, also close the search
      if (prev === true) {
        setSearchOpen(false);
        setSearchQuery("");
      }
      return !prev;
    });
  }, []);

  // Listen for custom event to close sidebar when documents are opened
  useEffect(() => {
    const handleCloseSidebar = () => {
      setSidebarOpen(false);
    };

    const handleOpenQuizPanel = (event) => {
      const { data, title } = event.detail;
      // Always use EnhancedQuizDeck for better UI - open as a closeable tab
      openComponentTab(<EnhancedQuizDeck quiz={data} />, title || "Quiz");
    };

    const handleOpenDocumentLibrary = () => {
      setShowDocumentLibrary(true);
      setSidebarOpen(false);
    };

    const handleOpenQuotesViewer = (event) => {
      const { quotesData, documentName } = event.detail;
      addContentTab(
        "quotes",
        quotesData,
        `Key Quotes - ${documentName}`,
        documentName,
      );
    };

    window.addEventListener("closeSidebar", handleCloseSidebar);
    window.addEventListener("openDocumentLibrary", handleOpenDocumentLibrary);
    window.addEventListener("openQuizPanel", handleOpenQuizPanel);
    window.addEventListener("openQuotesViewer", handleOpenQuotesViewer);
    // deprecated: openAssignmentAutomation now routed to AssignmentBuilder

    return () => {
      window.removeEventListener("closeSidebar", handleCloseSidebar);
      window.removeEventListener(
        "openDocumentLibrary",
        handleOpenDocumentLibrary,
      );
      window.removeEventListener("openQuizPanel", handleOpenQuizPanel);
      window.removeEventListener("openQuotesViewer", handleOpenQuotesViewer);
    };
  }, [openComponentTab, addContentTab]);

  // Persist sidebar state to localStorage and global state
  useEffect(() => {
    setLocalStorageItem("sidebarOpen", JSON.stringify(sidebarOpen));
    // Also update global state for immediate access during re-mounts
    window.globalSidebarState = sidebarOpen;
  }, [sidebarOpen, setLocalStorageItem]);

  // Listen for quick actions from SidePanel (Ask/Summarize) and prefill chat input
  useEffect(() => {
    const handler = (e) => {
      try {
        const { action, text } = (e && e.detail) || {};
        if (!text) return;

        let prompt = text;
        if (action === "summarize") {
          prompt = `Summarize the following selection:\n\n${text}`;
        } else if (action === "ask") {
          prompt = `Answer the following question or clarify this selection:\n\n${text}`;
        }

        setInput(prompt);

        // Focus the chat textarea and place cursor at end
        const el = textareaRef.current;
        if (el) {
          el.focus();
          if (typeof el.selectionStart === "number") {
            const len = el.value.length;
            el.selectionStart = len;
            el.selectionEnd = len;
          }
        }
      } catch {}
    };

    window.addEventListener("sidepanelTextAction", handler);
    return () => window.removeEventListener("sidepanelTextAction", handler);
  }, [setInput]);

  // Prevent unnecessary refreshes when switching tabs and preserve scroll position
  useEffect(() => {
    let savedScrollPosition = 0;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        // Save current state when tab becomes hidden
        setIsTabSwitching(true);

        // Save sidebar state
        localStorage.setItem("sidebarOpen", JSON.stringify(sidebarOpen));

        // Save scroll position
        if (chatAreaRef.current) {
          savedScrollPosition = chatAreaRef.current.scrollTop;
          setLocalStorageItem(
            `scrollPosition_${activeChat}`,
            savedScrollPosition.toString(),
          );
        }
      } else if (document.visibilityState === "visible") {
        // Restore sidebar state immediately
        const savedSidebarState = localStorage.getItem("sidebarOpen");
        if (savedSidebarState !== null) {
          const shouldBeOpen = JSON.parse(savedSidebarState);
          if (shouldBeOpen !== sidebarOpen) {
            setSidebarOpen(shouldBeOpen);
          }
        }

        // Restore scroll position
        setTimeout(() => {
          if (chatAreaRef.current && activeChat) {
            const saved = getLocalStorageItem(`scrollPosition_${activeChat}`);
            if (saved) {
              const scrollPos = parseInt(saved);
              chatAreaRef.current.scrollTop = scrollPos;
            }
          }
          setIsTabSwitching(false);
        }, 100); // Small delay to ensure content is rendered
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeChat, getLocalStorageItem, setLocalStorageItem, sidebarOpen]);

  // Handle chat selection
  const handleChatSelect = (chatId, chatName) => {
    // Save current messages
    if (activeChat) {
      setLocalStorageItem(
        `chatMessages_${activeChat}`,
        JSON.stringify(messages),
      );
    }

    // Load messages for selected chat
    const savedMessages = getLocalStorageItem(`chatMessages_${chatId}`);
    setMessages(savedMessages ? JSON.parse(savedMessages) : []);

    // Update active chat
    setActiveChat(chatId);
  };

  // Handle chat deletion
  const handleDeleteChat = (chatId, event) => {
    // Prevent the click from bubbling to parent elements
    event.stopPropagation();

    // Remove chat from the list
    setChats((prevChats) => {
      const updatedChats = prevChats.filter((chat) => chat.id !== chatId);
      setLocalStorageItem("chats", JSON.stringify(updatedChats));
      return updatedChats;
    });

    // Remove chat messages from localStorage
    removeLocalStorageItem(`chatMessages_${chatId}`);

    // If this was the active chat, select another chat or clear
    if (activeChat === chatId) {
      const remainingChats = chats.filter((chat) => chat.id !== chatId);
      if (remainingChats.length > 0) {
        handleChatSelect(remainingChats[0].id, remainingChats[0].name);
      } else {
        setActiveChat(null);
        setMessages([]);
        removeLocalStorageItem("activeChat");
      }
    }
  };

  // Handle edit chat title button click
  const handleEditChatTitle = (chatId, currentTitle, event) => {
    // Prevent the click from bubbling to parent elements
    event.stopPropagation();

    setEditingChatId(chatId);
    setEditingTitle(currentTitle);
  };

  // Create new chat
  const createNewChat = () => {
    const newChatId = `chat-${Date.now()}`;
    const newChat = {
      id: newChatId,
      name: "New Chat",
      isNew: true, // Flag for animation
    };

    // Add to chats list
    setChats((prevChats) => {
      const updatedChats = [...prevChats, newChat];
      setLocalStorageItem("chats", JSON.stringify(updatedChats));
      return updatedChats;
    });

    // Save current messages if in a chat
    if (activeChat) {
      setLocalStorageItem(
        `chatMessages_${activeChat}`,
        JSON.stringify(messages),
      );
    }

    // Set as active chat and clear messages
    setActiveChat(newChatId);
    setMessages([]);

    // Save empty messages array for new chat
    setLocalStorageItem(`chatMessages_${newChatId}`, JSON.stringify([]));
    setLocalStorageItem("activeChat", newChatId);

    // Remove the isNew flag after animation completes
    setTimeout(() => {
      setChats((prevChats) => {
        const updatedChats = prevChats.map((c) =>
          c.id === newChatId ? { ...c, isNew: false } : c,
        );
        setLocalStorageItem("chats", JSON.stringify(updatedChats));
        return updatedChats;
      });
    }, 1000); // Match this with CSS animation duration
  };

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  // Handle clicking a suggested prompt
  const handlePromptClick = (prompt) => {
    setInput(prompt);
    // Create a new chat if none is active
    if (!activeChat) {
      const newChatId = `chat-${Date.now()}`;
      const newChat = {
        id: newChatId,
        name: prompt.length > 20 ? `${prompt.substring(0, 20)}...` : prompt,
      };
      setChats((prevChats) => {
        const updatedChats = [...prevChats, newChat];
        setLocalStorageItem("chats", JSON.stringify(updatedChats));
        return updatedChats;
      });
      setActiveChat(newChatId);
      // Clear messages for new chat
      setMessages([]);
    }
    // Pass null for event when called from prompt click
    handleSendMessage(null, prompt);
  };

  // Open Document Library for file management
  const handleFileIconClick = () => {
    handleFileAttachment();
  };

  // Quick action: Create Quiz without using the format dropdown
  const handleQuickCreateQuiz = () => {
    // Use current input as topic; fallback generic phrasing
    const topic = (input || "this topic").trim();
    // Send a concise instruction; force quiz formatting in the request
    const prompt =
      topic === "this topic" ? "Create a quiz" : `Create a quiz about ${topic}`;
    handleSendMessage(null, prompt, "Quiz");
  };

  // Quick action: Create Flashcards without using format dropdown
  const handleQuickCreateFlashcards = () => {
    const topic = (input || "this topic").trim();
    const prompt =
      topic === "this topic"
        ? "Create flashcards"
        : `Create flashcards about ${topic}`;
    handleSendMessage(null, prompt, "Flashcards");
  };

  // Quick action: Create a Practice Test
  const handleQuickCreateTest = () => {
    setShowTestParams(true);
  };

  const handleConfirmTestParams = ({ count, type }) => {
    const topic = (input || "this topic").trim();
    const typeText =
      type === "Multiple Choice"
        ? "multiple choice questions"
        : type === "Short Response"
          ? "short response questions"
          : type === "Fill in the Blank"
            ? "fill in the blank questions"
            : "a mix of multiple choice, short response, and fill in the blank questions";

    const base =
      topic === "this topic"
        ? "Create a practice test"
        : `Create a practice test about ${topic}`;
    const prompt = `${base} with ${count} ${typeText}`;
    setShowTestParams(false);
    handleSendMessage(null, prompt, "Test");
  };

  // Handle file attachment - opens Document Library modal
  const handleFileAttachment = () => {
    setShowDocumentLibrary(true);
  };

  /* Background-index a PDF: call /api/process_document and merge the result
     (extractedText + per-page `pages`) onto the document in both the
     available and attached lists. Makes the first chat after upload
     instant and unlocks page-level citations. */
  const indexDocInBackground = useCallback(async (doc) => {
    const matches = (d) =>
      (doc.id != null && d.id === doc.id) ||
      (doc.id == null && d.name === doc.name);
    const patchDoc = (patch) => {
      setAvailableDocuments((prev) =>
        prev.map((d) => (matches(d) ? { ...d, ...patch } : d)),
      );
      setAttachedDocuments((prev) =>
        prev.map((d) => (matches(d) ? { ...d, ...patch } : d)),
      );
    };

    try {
      // Rebuild a File from the base64 content the backend returned.
      if (!doc.content || typeof doc.content !== "string") {
        throw new Error("Missing base64 content on uploaded doc");
      }
      const byteChars = atob(doc.content);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        bytes[i] = byteChars.charCodeAt(i);
      }
      const file = new File([bytes], doc.name, {
        type: doc.type || "application/pdf",
      });
      const fd = new FormData();
      fd.append("file", file);

      const res = await fetch(`${API_BASE}/api/process_document`, {
        method: "POST",
        body: fd,
      });
      if (!res.ok) {
        throw new Error(`process_document returned ${res.status}`);
      }
      const processData = await res.json();

      const pages = Array.isArray(processData.pages) ? processData.pages : null;
      const extractedText = pages
        ? pages.map((p) => p?.text || "").join("\n\n")
        : processData.text || "";

      if (!extractedText || extractedText.trim().length < 50) {
        throw new Error("Document has no readable text (scanned PDF?).");
      }

      patchDoc({
        extractedText,
        pages: pages || undefined,
        isIndexing: false,
        indexError: undefined,
      });
    } catch (err) {
      console.warn("Background indexing failed for", doc.name, err);
      patchDoc({ isIndexing: false, indexError: err.message || "failed" });
    }
  }, []);

  /* Attach a doc to chat context. If it's a PDF that hasn't been indexed
     yet (no extractedText/textContent), tag it isIndexing and kick off
     /api/process_document in the background so the first chat is instant. */
  const attachDocWithIndexing = useCallback(
    (doc) => {
      if (!doc) return;
      const isPdf =
        doc.type === "application/pdf" ||
        doc.name?.toLowerCase().endsWith(".pdf");
      const hasText =
        (doc.textContent && doc.textContent.trim().length > 50) ||
        (doc.extractedText && doc.extractedText.trim().length > 50);
      const needsIndexing = isPdf && !hasText && !!doc.content;

      const docToAttach = needsIndexing ? { ...doc, isIndexing: true } : doc;

      console.log(
        "[attach] doc:",
        doc.name,
        "| isPdf:",
        isPdf,
        "| hasText:",
        hasText,
        "| needsIndexing:",
        needsIndexing,
      );

      setAttachedDocuments((prev) => {
        // Avoid attaching the same doc twice
        const already = prev.some(
          (d) =>
            (doc.id != null && d.id === doc.id) ||
            (doc.id == null && d.name === doc.name),
        );
        return already ? prev : [...prev, docToAttach];
      });

      if (needsIndexing) indexDocInBackground(docToAttach);
    },
    [indexDocInBackground],
  );

  // Auto-fetch document insights when a new PDF is attached
  useEffect(() => {
    const pdfs = attachedDocuments.filter(
      (d) =>
        (d.type === "application/pdf" ||
          d.name?.toLowerCase().endsWith(".pdf")) &&
        d.content,
    );
    if (!pdfs.length) return;
    const latest = pdfs[pdfs.length - 1];
    const key = latest.id || latest.name;
    if (fetchedInsightsRef.current.has(key)) return;
    fetchedInsightsRef.current.add(key);
    setDocInsights({ docName: latest.name, loading: true, data: null });

    // Convert base64 to File and POST to /api/document_insights
    (async () => {
      try {
        const byteChars = atob(latest.content);
        const bytes = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++)
          bytes[i] = byteChars.charCodeAt(i);
        const file = new File([bytes], latest.name, {
          type: latest.type || "application/pdf",
        });
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${API_BASE}/api/document_insights`, {
          method: "POST",
          body: fd,
        });
        if (res.ok) {
          const data = await res.json();
          setDocInsights({ docName: latest.name, loading: false, data });
        } else {
          setDocInsights(null);
        }
      } catch {
        setDocInsights(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedDocuments.length]);

  /* ── Workspace / Highlight-to-Explain ──────────────────────────────
     State for the hero feature: open a doc in the side panel, highlight
     any passage, stream an explanation back. Explanations accumulate in
     `explanationItems` (newest first), rendered by HighlightExplainerPanel.
     ────────────────────────────────────────────────────────────────── */
  const [explanationItems, setExplanationItems] = useState([]);
  const [workspaceSessionOpen, setWorkspaceSessionOpen] = useState(false);

  // Re-render the companion "Lumi" tab whenever items change. The side-panel
  // de-dupes by title, so re-calling openComponentTab updates in place.
  useEffect(() => {
    if (!workspaceSessionOpen) return;
    openComponentTab(
      <HighlightExplainerPanel items={explanationItems} />,
      "Lumi",
    );
  }, [explanationItems, workspaceSessionOpen, openComponentTab]);

  const handleExplainStart = useCallback((meta) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setExplanationItems((prev) => [{ id, status: "loading", meta }, ...prev]);
  }, []);

  const handleExplanationResult = useCallback((result) => {
    setExplanationItems((prev) => {
      // Mark the most recent loading item as done; if none, prepend one.
      const idx = prev.findIndex((it) => it.status === "loading");
      if (idx === -1) {
        return [
          {
            id: `${Date.now()}`,
            status: "done",
            meta: result?.source || {},
            result,
          },
          ...prev,
        ];
      }
      const updated = [...prev];
      updated[idx] = { ...updated[idx], status: "done", result };
      return updated;
    });
  }, []);

  const openWorkspaceForDoc = useCallback(
    (doc) => {
      if (!doc) return;
      if (!doc.content || typeof doc.content !== "string") {
        console.warn(
          "[workspace] doc has no base64 content, can't open reader:",
          doc.name,
        );
        return;
      }
      setWorkspaceSessionOpen(true);
      // Open the explainer panel first so it shows behind the reader
      openComponentTab(
        <HighlightExplainerPanel items={explanationItems} />,
        "Lumi",
      );
      // Then the PDF reader — becomes the active tab
      openComponentTab(
        <PdfHighlightReader
          doc={doc}
          onExplainStart={handleExplainStart}
          onExplanation={handleExplanationResult}
        />,
        "Workspace",
      );
    },
    [
      openComponentTab,
      explanationItems,
      handleExplainStart,
      handleExplanationResult,
    ],
  );

  /* Safety net: whenever attachedDocuments changes, auto-index any PDF
     that still has no extractedText and isn't already being indexed.
     This catches docs that were added via any pathway (library modal,
     picker, filesUpdated event, direct state mutation) without going
     through attachDocWithIndexing. */
  const attachedDocsSignature = attachedDocuments
    .map(
      (d) =>
        `${d?.id || d?.name}:${!!d?.extractedText}:${!!d?.indexError}:${!!d?.isIndexing}`,
    )
    .join("|");
  useEffect(() => {
    if (!attachedDocuments || attachedDocuments.length === 0) return;

    attachedDocuments.forEach((doc) => {
      if (!doc) return;
      const isPdf =
        doc.type === "application/pdf" ||
        doc.name?.toLowerCase().endsWith(".pdf");
      if (!isPdf) return;
      const hasText =
        (doc.textContent && doc.textContent.trim().length > 50) ||
        (doc.extractedText && doc.extractedText.trim().length > 50);
      if (hasText) return;
      if (doc.isIndexing || doc.indexError) return;
      if (!doc.content || typeof doc.content !== "string") {
        console.warn(
          "[auto-index] skipping",
          doc.name,
          "— no base64 content on doc object",
        );
        return;
      }

      console.log("[auto-index] queueing", doc.name);

      // Flip isIndexing on both lists so the chip shows a spinner
      const matches = (d) =>
        (doc.id != null && d.id === doc.id) ||
        (doc.id == null && d.name === doc.name);
      setAttachedDocuments((prev) =>
        prev.map((d) => (matches(d) ? { ...d, isIndexing: true } : d)),
      );
      setAvailableDocuments((prev) =>
        prev.map((d) => (matches(d) ? { ...d, isIndexing: true } : d)),
      );

      // Kick off extraction
      indexDocInBackground({ ...doc, isIndexing: true });
    });
    // We only want this to react to additions or to indexError clears,
    // so depend on a pre-computed signature. Including the full array
    // causes re-runs for every in-place patch we do above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedDocsSignature]);

  // Handle file upload from the hidden input
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach((file) => formData.append("documents", file));

    try {
      const response = await fetch(`${API_BASE}/api/parse_documents`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (data.success && data.documents) {
        // Tag PDFs that still need text extraction so the UI can show
        // a subtle indexing indicator and chat can skip them until ready.
        const tagged = data.documents.map((doc) => {
          const isPdf =
            doc.type === "application/pdf" ||
            doc.name?.toLowerCase().endsWith(".pdf");
          const hasText =
            (doc.textContent && doc.textContent.trim().length > 50) ||
            (doc.extractedText && doc.extractedText.trim().length > 50);
          return isPdf && !hasText ? { ...doc, isIndexing: true } : doc;
        });

        setAvailableDocuments((prev) => [...prev, ...tagged]);
        setAttachedDocuments((prev) => [...prev, ...tagged]);

        // Fire-and-forget: index each pending PDF in the background.
        tagged.forEach((doc) => {
          if (doc.isIndexing) indexDocInBackground(doc);
        });
      }
    } catch (err) {
      console.error("Upload failed:", err);
    }

    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Save messages to localStorage whenever they change or active chat changes
  useEffect(() => {
    if (activeChat) {
      setLocalStorageItem(
        `chatMessages_${activeChat}`,
        JSON.stringify(messages),
      );
      setLocalStorageItem("activeChat", activeChat);
    }
  }, [messages, activeChat, setLocalStorageItem]);

  // Improved scrolling behavior
  useEffect(() => {
    if (!chatAreaRef.current || messages.length === 0) return;

    // Get the last message
    const lastMessage = messages[messages.length - 1];

    // If the last message is from the user, scroll to show it completely
    if (lastMessage.type === "user") {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
    // If the last message is from the assistant, scroll to show just the beginning
    else if (lastMessage.type === "assistant") {
      // Find the last user message index
      const lastUserMsgIndex = [...messages]
        .reverse()
        .findIndex((msg) => msg.type === "user");

      if (lastUserMsgIndex !== -1) {
        // Get the actual index in the messages array
        const userMsgIndex = messages.length - 1 - lastUserMsgIndex;

        // Find the DOM element for the user message
        const userMsgElement = document.getElementById(
          `message-${messages[userMsgIndex].id}`,
        );

        if (userMsgElement) {
          // Scroll to position the user message at the top of the view
          userMsgElement.scrollIntoView({ behavior: "smooth", block: "start" });
        } else {
          // Fallback if element not found
          chatAreaRef.current.scrollTop =
            chatAreaRef.current.scrollHeight - 300;
        }
      } else {
        // If no user message found, use default scroll behavior
        chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight - 300;
      }
    }
  }, [messages]);

  // Reset textarea height when input is empty
  useEffect(() => {
    if (input === "" && textareaRef.current) {
      textareaRef.current.style.height = "48px";
    }
  }, [input]);
  // Auto-resize the textarea as content grows (debounced to avoid layout thrash in Chrome)
  const resizeTimer = useRef(null);
  const autoResizeTextarea = useCallback((textarea) => {
    if (!textarea) return;
    textarea.style.height = "48px"; // Reset to default height
    const newHeight = Math.min(textarea.scrollHeight, 120);
    textarea.style.height = `${newHeight}px`;

    // If content is minimal, reset to default height
    if (
      textarea.value.length === 0 ||
      (textarea.value.split("\n").length === 1 && textarea.value.length < 50)
    ) {
      textarea.style.height = "48px";
    }
  }, []);

  const queueResize = useCallback(
    (element) => {
      if (!element) return;
      if (resizeTimer.current) cancelAnimationFrame(resizeTimer.current);
      resizeTimer.current = requestAnimationFrame(() => {
        autoResizeTextarea(element);
      });
    },
    [autoResizeTextarea],
  );

  useEffect(() => {
    return () => {
      if (resizeTimer.current) cancelAnimationFrame(resizeTimer.current);
    };
  }, []);

  // Handle key press in the input field
  const handleKeyPress = (e) => {
    // If Enter is pressed without Shift, send the message
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
    // If Shift+Enter, allow default behavior (new line)
    queueResize(textareaRef.current);
  };

  // Consume a pending message from another view (e.g. canvas "Ask Lumi")
  useEffect(() => {
    if (!pendingMessage) return;
    const timer = setTimeout(() => {
      handleSendMessage(null, pendingMessage);
      if (onPendingMessageConsumed) onPendingMessageConsumed();
    }, 150);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingMessage]);

  // Append text to the chat input from another view (e.g. PDF → queue for Lumi without switching away first)
  useEffect(() => {
    if (!pendingChatAppend) return;
    setInput((prev) => {
      const p = (prev || "").trim();
      const a = String(pendingChatAppend || "").trim();
      if (!a) return prev;
      return p ? `${p}\n\n---\n\n${a}` : a;
    });
    if (onPendingChatAppendConsumed) onPendingChatAppendConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingChatAppend]);

  // Handle continue response for incomplete AI messages
  const handleContinue = (message) => {
    // Get the last few words of the message to provide context
    const messageText =
      typeof message.content === "string" ? message.content : "";
    const lastWords = messageText.split(" ").slice(-10).join(" ");

    // Create a more specific continue prompt
    const continuePrompt = `Continue from where you left off. Your last response ended with: "${lastWords}"`;

    // Instead of automatically sending, set the input value for the user to edit if desired
    setInput(continuePrompt);

    // Focus the input field
    const inputField = document.querySelector(`.${styles.chatInputField}`);
    if (inputField) {
      inputField.focus();
    }

    // Scroll to the bottom to show the input field
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  // Handle quiz completion and quiz suggestion responses
  const handleQuizComplete = (results, quizData) => {
    // Handle quiz suggestion responses
    if (results.type === "startQuiz") {
      // User clicked "Yes" to start a quiz
      const newMessage = {
        id: Date.now(),
        type: "quiz",
        quizData: results.quizData,
      };

      // Add the quiz message to the chat
      setMessages((prev) => [...prev, newMessage]);
      return;
    }

    if (results.type === "skipQuiz") {
      // User clicked "No" to skip the quiz
      const skipMessage = {
        id: Date.now(),
        content: "No problem! Let me know if you have any other questions.",
        type: "assistant",
      };

      // Add the skip message to the chat
      setMessages((prev) => [...prev, skipMessage]);
      return;
    }

    // Handle regular quiz completion
    // Save quiz results to local storage if user is logged in
    if (user?.id && quizData?.id) {
      QuizIntegration.saveQuizResults(user.id, quizData.id, results);
    }

    // Set the quiz results in state if we have quizData
    if (quizData?.id) {
      setQuizResults((prev) => ({
        ...prev,
        [quizData.id]: results,
      }));
    }

    // Generate a completion message
    const completionMessage = {
      id: Date.now(),
      content: QuizIntegration.generateQuizCompletionResponse(results),
      type: "assistant",
    };

    // Add the completion message to the chat
    setMessages((prev) => [...prev, completionMessage]);

    // Scroll to the bottom
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 10);
  };

  // Helper: extract JSON from fenced code blocks like ```quiz ...``` or ```flashcards ...```
  const extractFencedJson = (text, lang) => {
    if (typeof text !== "string") return null;
    const pattern = new RegExp("```" + lang + "\\n([\\s\\S]*?)```", "i");
    const m = text.match(pattern);
    if (!m) return null;
    try {
      return JSON.parse(m[1]);
    } catch {
      return null;
    }
  };

  // Helper: try to interpret assistant raw text into quiz/flashcards data
  const parseLearningPayload = (rawText) => {
    // 1) Preferred: fenced quiz
    const quizJson = extractFencedJson(rawText, "quiz");
    if (quizJson && Array.isArray(quizJson.questions)) {
      const cards = quizJson.questions.map((q, idx) => {
        const id = q.id ?? String(idx + 1);
        const options = q.choices || [];
        const correctIdx =
          Array.isArray(q.correctAnswers) && q.correctAnswers.length > 0
            ? q.correctAnswers[0]
            : null;
        const answer =
          correctIdx != null && options[correctIdx] != null
            ? options[correctIdx]
            : null;
        return {
          id,
          question: q.prompt || "",
          options,
          answer,
          explanation: q.explanation || "",
        };
      });
      return {
        kind: "quiz",
        data: {
          title: quizJson.title || "Quiz",
          description: quizJson.description || "",
          cards,
        },
      };
    }
    // 2) Preferred: fenced flashcards
    const fcJson = extractFencedJson(rawText, "flashcards");
    if (fcJson && (Array.isArray(fcJson) || Array.isArray(fcJson.cards))) {
      const rawCards = Array.isArray(fcJson) ? fcJson : fcJson.cards;
      const cards = rawCards.map((c, idx) => ({
        id: c.id ?? String(idx + 1),
        question: c.front ?? c.question ?? "",
        answer: c.back ?? c.answer ?? "",
        explanation: c.explanation || "",
      }));
      const title = fcJson.title || "Flashcards";
      const description = fcJson.description || "";
      return { kind: "flashcard", data: { title, description, cards } };
    }
    // 3) Nothing structured
    return null;
  };

  // Stop streaming function - allows users to halt AI responses
  const handleInsertToNote = useCallback(
    (content, label = "AI Response") => {
      // Preferred path: direct inserter registered by NotebookPage in AppShell
      const ok = insertIntoActiveNote(content, label);
      if (ok) return;

      // Fallback: legacy localStorage hop (still useful if chat opens before
      // any note is mounted, e.g. cold-load on /notebook)
      try {
        const payload = { content, label, timestamp: Date.now() };
        localStorage.setItem(
          "notebook_pending_insert",
          JSON.stringify(payload),
        );
        if (window.__notebookToast) {
          window.__notebookToast(`"${label}" queued for your next note`);
        }
      } catch (e) {
        console.error("Insert to note failed:", e);
      }
    },
    [insertIntoActiveNote],
  );

  const handleStopStreaming = () => {
    // Clear all streaming intervals
    streamingIntervals.forEach((interval) => {
      clearInterval(interval);
    });
    setStreamingIntervals(new Set());

    // Mark all streaming messages as complete
    setMessages((prev) =>
      prev.map((msg) =>
        msg.isStreaming ? { ...msg, isStreaming: false } : msg,
      ),
    );

    // Reset streaming state
    setIsStreaming(false);
    setIsThinking(false);
    setCurrentStreamId(null);

    // Clear thinking message cycle interval
    if (window.thinkingCycleInterval) {
      clearInterval(window.thinkingCycleInterval);
      window.thinkingCycleInterval = null;
    }
  };

  // Handle sending a new message
  const handleSendMessage = async (e, promptText, forceFormat) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    const messageText = promptText || input;
    if (!messageText.trim()) return;

    // Detect user intent and auto-select format if not explicitly chosen
    const { intent, modifiers } = detectIntent(messageText);

    // Build structured instruction if a learning format is selected or inferred by intent
    let finalMessageText = messageText;
    let requestedFormat = forceFormat || selectedFormat;
    if (!requestedFormat) {
      if (intent === "create_quiz") requestedFormat = "Quiz";
      else if (intent === "make_flashcards") requestedFormat = "Flashcards";
    }
    if (requestedFormat === "Quiz") {
      const qty = modifiers?.numQuestions
        ? `Generate ${modifiers.numQuestions} questions. `
        : "";
      const diff = modifiers?.difficulty
        ? `Difficulty: ${modifiers.difficulty}. `
        : "";
      finalMessageText = `${messageText}\n\n${qty}${diff}Please produce ONLY a fenced code block labeled quiz with valid JSON. Do not add prose outside the fence.\nThe JSON schema must be: {\n  "title": string,\n  "questions": [\n    {"id": string|number, "prompt": string, "choices": [string,...], "correctAnswers": [number], "explanation"?: string}\n  ]\n}.\nReturn: \n\n\`\`\`quiz\n{...}\n\`\`\``;
    } else if (requestedFormat === "Flashcards") {
      const qty = modifiers?.numQuestions
        ? `Generate about ${modifiers.numQuestions} cards. `
        : "";
      finalMessageText = `${messageText}\n\n${qty}Please produce ONLY a fenced code block labeled flashcards with valid JSON. Do not add prose outside the fence.\nThe JSON may be either an array of cards or an object {"title": string, "cards": [{"front": string, "back": string}...]}.\nReturn: \n\n\`\`\`flashcards\n{...}\n\`\`\``;
    } else if (requestedFormat === "Test") {
      // Test mode uses same schema as quiz but fenced as `test` for exam-like rendering
      finalMessageText = `${messageText}\n\nCreate a practice test. Please produce ONLY a fenced code block labeled test with valid JSON. Do not add prose outside the fence.\nThe JSON schema must be: {\n  "title": string,\n  "questions": [\n    {"id": string|number, "prompt": string, "choices": [string,...], "correctAnswers": [number], "explanation"?: string}\n  ]\n}.\nPrefer 10-20 mixed-difficulty questions.\nReturn: \n\n\`\`\`test\n{...}\n\`\`\``;
    }

    // Store the current chat ID to ensure response goes to the right chat
    const currentChatId = activeChat;

    // Add user message (show only the original input, not backend instructions)
    const userMessage = {
      id: Date.now(),
      content: messageText,
      type: "user",
    };

    // Add user message to bottom (newest at bottom)
    setMessages([...messages, userMessage]);
    setInput("");
    setSelectedFormat(""); // Clear format after sending (we rely on intent next time)

    // Auto-generate chat name if this is the first message in a new chat
    if (activeChat && messages.length === 0) {
      const chatTitle = generateChatTitle(messageText);
      setChats((prevChats) => {
        const updatedChats = prevChats.map((chat) =>
          chat.id === activeChat ? { ...chat, name: chatTitle } : chat,
        );
        setLocalStorageItem("chats", JSON.stringify(updatedChats));
        return updatedChats;
      });
    }

    // Show thinking indicator with smart loading messages that cycle
    const getLoadingMessages = () => {
      const hasDocuments = attachedDocuments.length > 0;
      const docCount = attachedDocuments.length;

      if (hasDocuments) {
        if (intent === "compare") {
          return [
            `Reading ${docCount} document${docCount > 1 ? "s" : ""} carefully`,
            "Extracting key passages",
            "Verifying quotes for accuracy",
            "Analyzing themes and concepts",
            "Finding similarities",
            "Identifying differences",
            "Cross-referencing content",
            "Preparing detailed comparison",
            "Adding citations",
          ];
        } else if (intent === "create_quiz") {
          return [
            `Reading document${docCount > 1 ? "s" : ""}`,
            "Identifying key concepts",
            "Generating quiz questions",
            "Creating answer choices",
            "Adding explanations",
          ];
        } else if (intent === "make_flashcards") {
          return [
            "Extracting key concepts",
            "Identifying important terms",
            "Creating flashcard pairs",
            "Organizing by topic",
          ];
        } else if (intent === "summarize") {
          return [
            `Analyzing document${docCount > 1 ? "s" : ""}`,
            "Identifying main ideas",
            "Extracting key points",
            "Organizing summary",
          ];
        } else {
          return [
            `Reading ${docCount} document${docCount > 1 ? "s" : ""}`,
            "Analyzing content",
            "Processing your request",
            "Preparing response",
          ];
        }
      } else {
        if (intent === "create_quiz") {
          return [
            "Understanding the topic",
            "Generating quiz questions",
            "Creating answer choices",
            "Adding explanations",
          ];
        } else if (intent === "make_flashcards") {
          return [
            "Identifying key concepts",
            "Creating flashcard pairs",
            "Organizing content",
          ];
        } else if (intent === "summarize") {
          return [
            "Analyzing content",
            "Identifying main ideas",
            "Preparing summary",
          ];
        } else if (intent === "compare") {
          return [
            "Analyzing your request",
            "Preparing comparison",
            "Organizing findings",
          ];
        } else if (intent === "explain") {
          return [
            "Understanding the topic",
            "Preparing explanation",
            "Organizing concepts",
          ];
        } else {
          return ["Thinking", "Processing your request", "Preparing response"];
        }
      }
    };

    let loadingMessages = getLoadingMessages();

    let msgIndex = 0;
    setThinkingMessage(loadingMessages[0]);
    setIsThinking(true);

    // Clear any existing interval
    if (window.thinkingCycleInterval) {
      clearInterval(window.thinkingCycleInterval);
    }

    // Cycle through messages every 2.5 seconds
    const cycleInterval = setInterval(() => {
      msgIndex = (msgIndex + 1) % loadingMessages.length;
      setThinkingMessage(loadingMessages[msgIndex]);
    }, 2500);

    // Store interval to clear later
    window.thinkingCycleInterval = cycleInterval;

    // Scroll to bottom immediately
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTo({
          top: chatContainerRef.current.scrollHeight,
          behavior: "smooth",
        });
      }
    }, 10);

    try {
      // Prepare context for backend (convert existing messages to role-based format)
      const context = messages.map((msg) => ({
        role: msg.type === "assistant" ? "assistant" : "user",
        content: msg.content,
      }));

      // Build document context from attached documents
      let documentContext = "";
      // Track per-doc extraction outcomes so we can warn the user clearly
      // if the model has nothing to go on (scanned PDFs, indexing still in
      // progress, or extraction errors).
      const extractionReport = []; // { name, chars, reason? }
      if (attachedDocuments.length > 0) {
        documentContext = "\n\n--- DOCUMENT CONTEXT ---\n";
        for (let index = 0; index < attachedDocuments.length; index++) {
          const doc = attachedDocuments[index];
          let docContent = "";
          let reason;

          if (doc.isIndexing) {
            reason = "still indexing — try again in a moment";
          } else if (doc.indexError) {
            reason = `indexing failed: ${doc.indexError}`;
          }

          // Try textContent first (for DOCX files)
          if (doc.textContent && typeof doc.textContent === "string") {
            docContent = doc.textContent;
          }
          // Try extractedText (if already extracted)
          else if (doc.extractedText && typeof doc.extractedText === "string") {
            docContent = doc.extractedText;
          }
          // For PDFs, try to extract text
          else if (
            doc.type === "application/pdf" ||
            doc.name?.toLowerCase().endsWith(".pdf")
          ) {
            try {
              docContent = await extractDocumentText(doc);
            } catch (err) {
              console.warn(`Could not extract text from ${doc.name}:`, err);
              docContent = "";
              reason = reason || err.message || "PDF extraction failed";
            }
          }
          // For text files, decode base64 content
          else if (
            doc.content &&
            typeof doc.content === "string" &&
            !doc.content.startsWith("JVBERi")
          ) {
            try {
              docContent = atob(doc.content);
            } catch {
              docContent = doc.content;
            }
          }

          const trimmedLen = (docContent || "").trim().length;
          extractionReport.push({
            name: doc.name || `Document ${index + 1}`,
            chars: trimmedLen,
            reason: trimmedLen > 0 ? undefined : reason || "no readable text",
          });

          if (trimmedLen > 0) {
            documentContext += `\n[Document ${index + 1}: ${
              doc.name
            }]\n${docContent.substring(0, 15000)}\n`;
          } else {
            documentContext += `\n[Document ${index + 1}: ${
              doc.name
            }] (Could not extract text content)\n`;
          }
        }
        documentContext += "\n--- END DOCUMENT CONTEXT ---\n\n";

        console.log(
          "[chat] document extraction:",
          extractionReport
            .map(
              (r) =>
                `${r.name} → ${r.chars} chars${
                  r.reason ? ` (${r.reason})` : ""
                }`,
            )
            .join(" | "),
        );

        // If every attached doc produced no readable text, short-circuit
        // with a clear inline message instead of letting the model answer
        // generically (the model has literally nothing to cite).
        const totalChars = extractionReport.reduce((s, r) => s + r.chars, 0);
        if (totalChars === 0) {
          // Clear the thinking indicator + loader interval
          try {
            if (window.thinkingCycleInterval) {
              clearInterval(window.thinkingCycleInterval);
              window.thinkingCycleInterval = null;
            }
          } catch {}
          setIsThinking(false);

          const stillIndexing = extractionReport.some((r) =>
            /still indexing/i.test(r.reason || ""),
          );
          const lines = extractionReport.map(
            (r) => `• **${r.name}** — ${r.reason}`,
          );
          const header = stillIndexing
            ? "I can't read your documents yet — they're still being indexed."
            : "I couldn't extract any readable text from your attached documents.";
          const hint = stillIndexing
            ? "Give it a second and try again. The spinner on the doc chip will disappear when it's ready."
            : "If it's a scanned PDF, it needs OCR (not yet supported). Try a text-based PDF, a Word doc, or paste the text directly.";

          const warnMessage = {
            id: Date.now() + 1,
            role: "assistant",
            type: "assistant",
            content: `${header}\n\n${lines.join("\n")}\n\n${hint}`,
          };
          if (currentChatId === activeChat) {
            setMessages((prev) => [...prev, warnMessage]);
          }
          return;
        }
      }

      // Inject global notebook sources (from SourcesContext via getExtraContext prop)
      const sourcesCtx =
        typeof getExtraContext === "function" ? getExtraContext() : "";

      // The /api/chat backend only consumes the `document_context` field
      // when strict_context is on. For the normal branch we inline the
      // context into the user message so the model actually sees it.
      // We still send document_context separately for strict mode.
      const combinedContext = [sourcesCtx, documentContext]
        .filter(Boolean)
        .join("\n\n");
      const messageForBackend = combinedContext
        ? `${combinedContext}\n---\n\nUser question:\n${finalMessageText}`
        : finalMessageText;

      const response = await fetch(LOCAL_API_URLS.CHAT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message: messageForBackend,
          context,
          document_context: documentContext,
        }),
      });

      // Rate limit handling
      if (response.status === 429) {
        const systemMsg = {
          id: Date.now() + 1,
          type: "system",
          content: "Only 300 messages per hour, please come back later.",
        };
        if (currentChatId === activeChat) {
          setMessages((prev) => [...prev, systemMsg]);
        } else {
          const chatMessages = getLocalStorageItem(
            `chatMessages_${currentChatId}`,
          );
          const parsedStored = chatMessages ? JSON.parse(chatMessages) : [];
          const updatedStored = [...parsedStored, systemMsg];
          setLocalStorageItem(
            `chatMessages_${currentChatId}`,
            JSON.stringify(updatedStored),
          );
        }
        return;
      }

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      const data = await response.json();
      let replyPayload = data.reply;

      // Normalize to a raw text we can parse
      let rawText;
      let meta;
      if (replyPayload && typeof replyPayload === "object") {
        rawText = replyPayload.text || replyPayload.content || "";
        meta = {
          confidence: replyPayload.confidence,
          format: replyPayload.format,
          responseType: replyPayload.type,
        };
      } else {
        rawText = typeof replyPayload === "string" ? replyPayload : "";
      }

      // Ensure rawText is always a string and clean it
      rawText = String(rawText || "");

      // Clean up any potential object references or malformed content
      if (rawText.includes("[object Object]")) {
        console.warn(
          "Detected [object Object] in response, attempting to clean...",
        );
        rawText = rawText.replace(/\[object Object\]/g, "");
      }

      // If a structured learning format was requested, try to parse structured payload
      let validatedFlag = false;
      if (requestedFormat === "Quiz" || requestedFormat === "Flashcards") {
        // Insert a rotating status message while we build the quiz
        const statusId = `status_${Date.now()}`;
        const phrases = [
          "Thinking.",
          "Thinking..",
          "Thinking...",
          "Gathering facts.",
          "Gathering facts..",
          "Gathering facts...",
          "Reasoning.",
          "Reasoning..",
          "Reasoning...",
          "Formatting questions.",
          "Formatting questions..",
          "Formatting questions...",
        ];
        let phraseIdx = 0;
        let statusTimer = null;
        const loaderStart = Date.now();
        const clearStatusMin = async () => {
          const elapsed = Date.now() - loaderStart;
          const wait = Math.max(0, 1200 - elapsed);
          if (wait) {
            await new Promise((r) => setTimeout(r, wait));
          }
          clearStatus();
        };
        const pushStatus = () => {
          if (currentChatId === activeChat) {
            setMessages((prev) => [
              ...prev,
              {
                id: statusId,
                type: "status",
                role: "assistant",
                content: phrases[phraseIdx],
              },
            ]);
            statusTimer = setInterval(() => {
              phraseIdx = (phraseIdx + 1) % phrases.length;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === statusId ? { ...m, content: phrases[phraseIdx] } : m,
                ),
              );
            }, 1200);
          }
        };
        const clearStatus = () => {
          try {
            if (statusTimer) clearInterval(statusTimer);
          } catch {}
          if (currentChatId === activeChat) {
            setMessages((prev) => prev.filter((m) => m.id !== statusId));
          }
        };

        pushStatus();
        const parsed = parseLearningPayload(rawText);
        dbg("Initial parse result", { kind: parsed?.kind });
        if (parsed && parsed.kind === "quiz") {
          const imm = parsed.data;
          const topic = QuizIntegration.extractTopic(messageText) || "";
          const ok = validateQuizQuality(imm, { topic });
          if (!ok) {
            // force retry below
          } else {
            validatedFlag = true;
            await clearStatusMin();
            validatedFlag = true;
            // Proceed to append assistant text so Message can parse and open side panel
          }
        }

        // If the response already contains an immersive or fenced learning block,
        // attempt to validate it and, if valid, mark as validated and clear loader.
        const hasImmersiveBlock =
          /<immersive>[\s\S]*?```\s*quiz[\s\S]*?```[\s\S]*?<\/immersive>/i.test(
            rawText || "",
          );
        const hasFencedLearning =
          /```\s*(quiz|flashcards|test)\s*\n[\s\S]*?```/i.test(rawText || "");
        if (hasImmersiveBlock || hasFencedLearning) {
          try {
            const fenceMatch = (rawText || "").match(
              /```\s*quiz\s*\n([\s\S]*?)```/i,
            );
            if (fenceMatch) {
              const obj = JSON.parse(fenceMatch[1]);
              const topic = QuizIntegration.extractTopic(messageText) || "";
              if (
                obj &&
                Array.isArray(obj.questions) &&
                validateQuizQuality(obj, { topic })
              ) {
                validatedFlag = true;
                await clearStatusMin();
              } else {
                // Critique-and-fix attempt
                dbg("Critique pass (pre-fenced content)");
                const fixed = await critiqueAndFixQuiz(rawText, topic);
                if (fixed) {
                  const reparsed = parseLearningPayload(fixed);
                  if (
                    reparsed?.kind === "quiz" &&
                    validateQuizQuality(reparsed.data, { topic })
                  ) {
                    rawText = fixed;
                    validatedFlag = true;
                    await clearStatusMin();
                  }
                }
              }
            }
          } catch {}
        } else {
          // Fallback: retry backend with stricter, topic-specific prompt before local generation
          try {
            const retryTopic =
              QuizIntegration.extractTopic(messageText) || messageText;
            let retryPrompt = messageText;
            if (requestedFormat === "Quiz") {
              retryPrompt = `${messageText}

You are generating a multiple-choice quiz about "${retryTopic}". Follow these strict rules:

REQUIREMENTS
- 100% domain-specific; no generic filler like "This question relates to the topic".
- Exactly 4 choices per question. No more, no less.
- Prefer 1 correct answer per question. If truly needed, allow multiple correct answers (then mark both indices).
- Provide concise, factual rationales:
  - question-level "explanation" summarizing the correct concept
  - per-choice "choiceRationales[i]" explaining why each option is correct/incorrect
- Match the requested question count and difficulty. If none provided, default to 10 questions, medium difficulty. Clamp count to 3..30.
- Use precise terminology; avoid ambiguous wording. No references, no markdown outside the fenced JSON.

OUTPUT FORMAT (JSON in a fenced code block labeled quiz):
\`\`\`quiz
{
  "title": string,
  "questions": [
    {
      "id": string | number,
      "prompt": string,
      "choices": [string, string, string, string],
      "correctAnswerIds": [number],       // indices into choices, zero-based
      "explanation": string,              // concise reason why the correct answer(s) is right
      "choiceRationales": [               // same length as choices
        string, string, string, string     // why each choice is right/wrong
      ],
      "hint": string | null
    }
  ]
}
\`\`\`

RETURN RULES
- Return ONLY the fenced JSON block labeled quiz. No extra text.
- Ensure the JSON is valid and parseable in a browser.
`;
            } else if (requestedFormat === "Flashcards") {
              retryPrompt = `${messageText}

You are generating topic-specific flashcards about "${retryTopic}". Follow these rules:
- Accurate, concise, domain-specific content; no placeholders.
- Provide at least 5 and at most 50 cards (respect user's requested count if any).
- Prefer simple Q/A style. Avoid markdown outside the fenced JSON.

OUTPUT (JSON in a fenced code block labeled flashcards):
\`\`\`flashcards
{
  "title": string,
  "cards": [ { "front": string, "back": string } ]
}
\`\`\`

Return ONLY the fenced JSON block.`;
            }

            const retryRes = await fetch(LOCAL_API_URLS.CHAT, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                message: retryPrompt,
                history: [],
                stream: false,
              }),
            });
            const retryData = await retryRes.json();
            const retryText =
              retryData?.reply || retryData?.content || retryData?.text || "";
            const reparsed = parseLearningPayload(retryText);
            dbg("Strict retry parse", { kind: reparsed?.kind });
            if (reparsed && reparsed.kind === "quiz") {
              const imm2 = reparsed.data;
              if (validateQuizQuality(imm2, { topic: retryTopic })) {
                // Use the stricter retry text as the assistant message so Message can parse it
                rawText = retryText;
                validatedFlag = true;
                await clearStatusMin();
              } else {
                // Critique-and-fix on strict retry
                dbg("Critique pass (strict retry)");
                const fixed2 = await critiqueAndFixQuiz(retryText, retryTopic);
                const reparsed2 = parseLearningPayload(fixed2);
                if (
                  reparsed2?.kind === "quiz" &&
                  validateQuizQuality(reparsed2.data, { topic: retryTopic })
                ) {
                  rawText = fixed2;
                  validatedFlag = true;
                  await clearStatusMin();
                }
              }
            }

            // As a last resort, DO NOT inject a default quiz to users. Show a friendly notice in side panel.
            if (requestedFormat === "Quiz") {
              await clearStatusMin();
              const errId = `status_err_${Date.now()}`;
              if (currentChatId === activeChat) {
                setMessages((prev) => [
                  ...prev,
                  {
                    id: errId,
                    type: "status",
                    role: "assistant",
                    content:
                      "Error: Could not generate a high-quality quiz right now. Please try again or rephrase your prompt.",
                  },
                ]);
              }
            } else if (requestedFormat === "Flashcards") {
              // Optionally inject flashcards fenced block; for now leave raw text unchanged
            }
          } catch (e) {
            // swallow
          }
        }
      }

      // Check if this is a quiz/test request - don't stream these
      const isQuizRequest =
        requestedFormat === "Quiz" ||
        requestedFormat === "Test" ||
        rawText.includes("```quiz") ||
        rawText.includes("```test");

      const assistantMessageId = Date.now() + 1;
      const assistantMessage = {
        id: assistantMessageId,
        role: "assistant",
        content: isQuizRequest ? rawText : "", // Show full content immediately for quizzes
        type: "assistant",
        meta: { ...(meta || {}), validated: validatedFlag },
        enhanced: meta,
        rawText,
        isStreaming: !isQuizRequest, // Don't stream quizzes
      };

      // Add message
      if (currentChatId === activeChat) {
        setMessages((prev) => [...prev, assistantMessage]);

        if (!isQuizRequest) {
          // Stream the content for non-quiz responses
          let currentIndex = 0;

          // Set streaming state
          setIsStreaming(true);
          setCurrentStreamId(assistantMessageId);

          const streamInterval = setInterval(() => {
            if (currentIndex < rawText.length) {
              // Stream faster - 8 characters at once
              currentIndex = Math.min(currentIndex + 8, rawText.length);

              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, content: rawText.slice(0, currentIndex) }
                    : msg,
                ),
              );
            } else {
              // Streaming complete
              clearInterval(streamInterval);
              setStreamingIntervals((prev) => {
                const newSet = new Set(prev);
                newSet.delete(streamInterval);
                return newSet;
              });

              // Mark as not streaming
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessageId
                    ? { ...msg, isStreaming: false }
                    : msg,
                ),
              );

              // Reset streaming state
              setIsStreaming(false);
              setCurrentStreamId(null);
            }
          }, 30); // 30ms delay between chunks

          // Track interval for cleanup
          setStreamingIntervals((prev) => new Set([...prev, streamInterval]));
        }
      } else {
        // For inactive chats, add complete message
        const completeMessage = {
          ...assistantMessage,
          content: rawText,
          isStreaming: false,
        };
        const chatMessages = getLocalStorageItem(
          `chatMessages_${currentChatId}`,
        );
        const parsed = chatMessages ? JSON.parse(chatMessages) : [];
        const updated = [...parsed, completeMessage];
        setLocalStorageItem(
          `chatMessages_${currentChatId}`,
          JSON.stringify(updated),
        );
      }
    } catch (err) {
      console.error("Chat error", err);
      const errorMessage = {
        id: Date.now() + 1,
        content: "Sorry, I couldn't reach the server. Please try again later.",
        type: "assistant",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsThinking(false);
      // Clear thinking message cycle interval
      if (window.thinkingCycleInterval) {
        clearInterval(window.thinkingCycleInterval);
        window.thinkingCycleInterval = null;
      }
    }
  };

  // Generate a chat title based on user's first message
  const generateChatTitle = (userInput) => {
    // Extract a meaningful title from the user input
    let title = userInput.trim();

    // Remove any markdown or special characters
    title = title.replace(/[*_`#]/g, "");

    // Get the first sentence or up to a reasonable length
    const firstSentence = title.split(/[.!?]\s/)[0];

    if (firstSentence && firstSentence.length > 3) {
      title = firstSentence.trim();
    }

    // Limit title length
    return title.length > 30 ? title.substring(0, 27) + "..." : title;
  };

  // Options menu click outside handler removed

  // Save active chat to localStorage whenever it changes
  useEffect(() => {
    if (activeChat) {
      setLocalStorageItem("activeChat", activeChat);
    }
  }, [activeChat, setLocalStorageItem]);

  // Save chats to localStorage whenever they change
  useEffect(() => {
    setLocalStorageItem("chats", JSON.stringify(chats));
  }, [chats, setLocalStorageItem]);

  // Cleanup streaming intervals when component unmounts or chat changes
  useEffect(() => {
    return () => {
      streamingIntervals.forEach((interval) => clearInterval(interval));
    };
  }, [activeChat, streamingIntervals]);

  // Mobile detection with resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close document library and other panels when sidebar collapses
  useEffect(() => {
    if (!sidebarOpen) {
      setShowDocumentLibrary(false);
    }
  }, [sidebarOpen]);

  return (
    <div
      className={`${styles.container} ${shellEmbedded ? styles.shellEmbedded : ""}`}
    >
      <TrialBanner />
      {/* No floating menu toggle needed */}

      <div className={styles.appLayout}>
        {/* Gemini-style Sidebar */}
        <div
          className={`${styles.sidebar} ${
            !sidebarOpen ? styles.collapsed : ""
          }`}
        >
          <div className={styles.sidebarContent}>
            {/* Mode Toggle - Chat vs Notes (hidden when AppShell top bar handles navigation) */}
            {!shellEmbedded && (
              <div className={styles.sidebarModeToggle}>
                <button
                  className={`${styles.modeToggleBtn} ${viewMode === "chat" ? styles.modeToggleBtnActive : ""}`}
                  onClick={() => switchViewMode("chat")}
                  title="Chat with Lumi"
                >
                  <MessageSquare size={16} />
                  <span className={styles.buttonText}>Chat</span>
                </button>
                <button
                  className={`${styles.modeToggleBtn} ${viewMode === "notes" ? styles.modeToggleBtnActive : ""}`}
                  onClick={() => switchViewMode("notes")}
                  title="Open Notebooks"
                >
                  <Book size={16} />
                  <span className={styles.buttonText}>Notes</span>
                </button>
              </div>
            )}

            {viewMode === "notes" ? (
              /* Notebook sidebar content — pushed from NotebookPage via context */
              <div
                className={`${styles.sidebarNav} ${!sidebarOpen ? styles.navHidden : ""}`}
                style={{ flex: 1, overflow: "hidden auto" }}
              >
                {notebookSidebarContent}
              </div>
            ) : (
              <>
                {/* Sidebar Header: collapse toggle + new chat */}
                <div className={styles.sidebarHeaderRow}>
                  <button
                    className={styles.sidebarToggleBtn}
                    onClick={toggleSidebar}
                    title={sidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
                  >
                    <Menu size={18} />
                  </button>
                  <button
                    className={styles.newChatIconBtn}
                    onClick={createNewChat}
                    title="New chat"
                  >
                    <Edit size={16} />
                  </button>
                </div>

                {/* Sidebar Navigation - fades in/out when expanded/collapsed */}
                <div
                  className={`${styles.sidebarNav} ${
                    !sidebarOpen ? styles.navHidden : ""
                  }`}
                >
                  <div className={styles.navSectionHeader}>
                    <h3 className={styles.sectionTitle}>Recent</h3>
                  </div>
                  <div className={styles.navSection}>
                    {/* Filter chats based on search query if one exists */}
                    {chats
                      .filter(
                        (chat) =>
                          !searchQuery ||
                          chat.name
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase()),
                      )
                      .map((chat) => (
                        <motion.div
                          key={chat.id}
                          className={`${styles.navItem} ${
                            activeChat === chat.id ? styles.activeNavItem : ""
                          } ${chat.isNew ? styles.newChatItem : ""}`}
                          initial={
                            chat.isNew ? { opacity: 0, y: -10 } : { opacity: 1 }
                          }
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                          {editingChatId === chat.id ? (
                            <div className={styles.chatTitleEditContainer}>
                              <input
                                type="text"
                                className={styles.chatTitleInput}
                                value={editingTitle}
                                onChange={(e) =>
                                  setEditingTitle(e.target.value)
                                }
                                onBlur={() => {
                                  // Save the edited title
                                  if (editingTitle.trim()) {
                                    setChats((prevChats) => {
                                      const updatedChats = prevChats.map((c) =>
                                        c.id === chat.id
                                          ? { ...c, name: editingTitle.trim() }
                                          : c,
                                      );
                                      localStorage.setItem(
                                        "chats",
                                        JSON.stringify(updatedChats),
                                      );
                                      return updatedChats;
                                    });
                                  }
                                  setEditingChatId(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.target.blur();
                                  } else if (e.key === "Escape") {
                                    e.preventDefault();
                                    setEditingChatId(null);
                                  }
                                }}
                                autoFocus
                              />
                            </div>
                          ) : (
                            <div className={styles.chatItemContainer}>
                              <button
                                className={styles.chatButton}
                                onClick={() =>
                                  handleChatSelect(chat.id, chat.name)
                                }
                              >
                                <span
                                  className={`${styles.navItemText} ${
                                    activeChat === chat.id
                                      ? styles.activeNavItemText
                                      : ""
                                  }`}
                                >
                                  {chat.name}
                                </span>
                              </button>

                              <div className={styles.chatActions}>
                                <button
                                  className={styles.chatActionButton}
                                  onClick={(e) =>
                                    handleEditChatTitle(chat.id, chat.name, e)
                                  }
                                  title="Edit chat title"
                                >
                                  <Edit3
                                    size={14}
                                    className={iconStyles.iconHoverGrow}
                                  />
                                </button>
                                <button
                                  className={styles.chatActionButton}
                                  onClick={(e) => handleDeleteChat(chat.id, e)}
                                  title="Delete chat"
                                >
                                  <Trash2
                                    size={14}
                                    className={iconStyles.iconHoverGrow}
                                  />
                                </button>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      ))}
                  </div>
                  <div ref={messagesEndRef} style={{ height: "1px" }} />
                </div>
              </>
            )}

            {/* Footer with stacked buttons */}
            <div className={styles.sidebarFooter}>
              <ResourcesSidebar isOpen={sidebarOpen} />
              {!shellEmbedded && (
                <button
                  className={styles.footerButton}
                  title={
                    isDarkMode ? "Switch to Light mode" : "Switch to Dark mode"
                  }
                  onClick={toggleTheme}
                >
                  {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                  <span className={styles.buttonText}>
                    {isDarkMode ? "Light mode" : "Dark mode"}
                  </span>
                </button>
              )}
              {!shellEmbedded && (
                <button
                  className={`${styles.footerButton}`}
                  title="Open Notebooks"
                  onClick={() => switchViewMode("notes")}
                >
                  <Book size={20} />
                  <span className={styles.buttonText}>Open Notes</span>
                </button>
              )}
              {!shellEmbedded && (
                <button
                  className={`${styles.footerButton} ${isCanvasOpen ? styles.footerButtonActive : ""}`}
                  title="Study Workspace"
                  onClick={() => {
                    if (savedWorkspaces.length > 0) {
                      openWorkspace(
                        savedWorkspaces[savedWorkspaces.length - 1].id,
                      );
                    } else {
                      createWorkspace("Study Workspace");
                    }
                  }}
                >
                  <Layers size={20} />
                  <span className={styles.buttonText}>Study Workspace</span>
                </button>
              )}
              <button
                className={styles.footerButton}
                title="Assignment Builder"
                onClick={() => setShowAssignmentBuilder(true)}
              >
                <FileText size={20} />
                <span className={styles.buttonText}>Assignment Builder</span>
              </button>
              {!shellEmbedded && (
                <button
                  className={styles.footerButton}
                  title="Sign Out"
                  onClick={signOut}
                >
                  <LogOut size={20} />
                  <span className={styles.buttonText}>Sign Out</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Backdrop - closes sidebar when tapped */}
        {isMobile && sidebarOpen && (
          <div
            className={styles.sidebarBackdrop}
            onClick={() => setSidebarOpen(false)}
            aria-label="Close sidebar"
          />
        )}

        {/* Quadrant Layout - renders when not in single mode */}
        {layout !== LAYOUTS.SINGLE && (
          <>
            {/* Hamburger menu moved to sidebar */}
            <QuadrantLayout
              documents={availableDocuments}
              sidebarCollapsed={!sidebarOpen}
              onUploadDocument={() => setShowDocumentLibrary(true)}
              onCloseAll={() => {
                // Layout will auto-reset to single
              }}
            />
          </>
        )}

        {/* Main Chat Interface - only show in single layout mode */}
        <div
          className={`${styles.mainChatSection} ${
            !sidebarOpen ? styles.sidebarCollapsed : ""
          } ${showAssignmentBuilder ? styles.builderOpen : ""}`}
          style={{ display: layout !== LAYOUTS.SINGLE ? "none" : undefined }}
        >
          {/* Hamburger menu moved to sidebar */}

          <div className={styles.chatContentWrapper}>
            {shellEmbedded &&
              viewMode === "chat" &&
              activeChat &&
              messages.length > 0 && (
                <div className={styles.geminiSessionTitle}>
                  <span className={styles.geminiSessionTitleText}>
                    {chats.find((c) => c.id === activeChat)?.name?.trim() ||
                      "Chat"}
                  </span>
                </div>
              )}
            <div className={styles.chatArea} ref={chatAreaRef}>
              <div
                className={styles.chatMessages}
                role="log"
                aria-live="polite"
                ref={chatContainerRef}
              >
                <div className={styles.messagesContainer}>
                  {messages.length > 0 ? (
                    <AnimatePresence>
                      {messages.map((message) => (
                        <Message
                          refillInput={setInput}
                          key={message.id}
                          message={message}
                          onQuizComplete={(results) =>
                            handleQuizComplete(results, message.quizData)
                          }
                          onContinue={handleContinue}
                          onOpenImmersive={handleOpenImmersive}
                          onInsertToNote={handleInsertToNote}
                        />
                      ))}
                    </AnimatePresence>
                  ) : null}
                </div>
                <div
                  className={styles.thinkingContainer}
                  style={{
                    display: isThinking && !isTabSwitching ? "flex" : "none",
                  }}
                >
                  <div className={styles.thinkingWrapper}>
                    <div className={styles.thinkingContent}>
                      <span className={styles.thinkingText}>
                        {thinkingMessage.replace(/\.{3}$/, "")}
                      </span>
                      <span className={styles.thinkingEllipsis}>
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div ref={chatEndRef} />
              </div>
            </div>

            {messages.length === 0 ? (
              <div className={styles.emptyStateWrapper}>
                <EmptyState
                  onPromptClick={handlePromptClick}
                  onAssignmentOpen={() => setShowAssignmentBuilder(true)}
                  quickActions={
                    <div className={styles.quickActionsRow}>
                      <div className={styles.quickActionWrapper}>
                        <button
                          className={styles.quickActionBtn}
                          onClick={() => setShowAssignmentBuilder(true)}
                        >
                          {renderIcon("add-circle")}
                          <span>Assignment</span>
                        </button>
                      </div>
                      <div className={styles.quickActionWrapper}>
                        <button
                          className={styles.quickActionBtn}
                          onClick={() =>
                            handlePromptClick("Create a quiz about this topic")
                          }
                        >
                          {renderIcon("chat-bubble")}
                          <span>Quiz</span>
                        </button>
                      </div>
                      <div className={styles.quickActionWrapper}>
                        <button
                          className={styles.quickActionBtn}
                          onClick={() =>
                            handlePromptClick("Create flashcards for studying")
                          }
                        >
                          {renderIcon("upload-cloud")}
                          <span>Flashcards</span>
                        </button>
                      </div>
                      <div className={styles.quickActionWrapper}>
                        <button
                          className={styles.quickActionBtn}
                          onClick={() =>
                            handlePromptClick(
                              "Create study notes from this content",
                            )
                          }
                        >
                          {renderIcon("file-check")}
                          <span>Notes</span>
                        </button>
                      </div>
                      <div className={styles.quickActionWrapper}>
                        <button
                          ref={layoutBtnRef}
                          className={`${styles.quickActionBtn} ${styles.layoutBtn} ${showLayoutSelector ? styles.quickActionBtnActive : ""}`}
                          onClick={() => setShowLayoutSelector((v) => !v)}
                        >
                          <Grid
                            size={18}
                            className={iconStyles.iconHoverGrow}
                          />
                          <span>Layout</span>
                        </button>
                        <LayoutSelector
                          isOpen={showLayoutSelector}
                          onClose={() => setShowLayoutSelector(false)}
                          anchorEl={layoutBtnRef.current}
                        />
                      </div>
                    </div>
                  }
                >
                  {/* Input box inside empty state */}
                  <div className={styles.emptyStateInputWrapper}>
                    <div
                      id="chatInputBar"
                      className={`${styles.chatInputBar} ${styles.emptyStateInput} ${
                        !isCollapsed ? styles.chatInputBarResponsive : ""
                      } ${input.trim() ? styles.active : ""} ${
                        isInputFocused ? styles.chatInputBarFocused : ""
                      }`}
                    >
                      <form
                        onSubmit={handleSendMessage}
                        className={styles.chatForm}
                      >
                        <div className={styles.inputWrapper}>
                          {attachedDocuments &&
                            attachedDocuments.length > 0 && (
                              <div className={styles.attachedDocsInline}>
                                {(attachedDocuments || []).map((doc, index) => (
                                  <div
                                    key={doc.id || doc.name || index}
                                    className={`${styles.attachedDocChip} ${
                                      doc.isIndexing
                                        ? styles.attachedDocChipIndexing
                                        : ""
                                    }`}
                                    title={
                                      doc.isIndexing
                                        ? "Indexing… (ready to chat once done)"
                                        : doc.indexError
                                          ? `Indexing failed: ${doc.indexError}`
                                          : doc.name
                                    }
                                  >
                                    {doc.isIndexing ? (
                                      <Loader2
                                        size={12}
                                        className={styles.attachedDocSpinner}
                                      />
                                    ) : (
                                      <FileText size={12} />
                                    )}
                                    <span className={styles.attachedDocName}>
                                      {(() => {
                                        const n = doc?.name || "Untitled";
                                        return n.length > 20
                                          ? n.substring(0, 17) + "..."
                                          : n;
                                      })()}
                                    </span>
                                    {(doc.type === "application/pdf" ||
                                      doc.name
                                        ?.toLowerCase()
                                        .endsWith(".pdf")) && (
                                      <button
                                        type="button"
                                        className={styles.workspaceDocBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openWorkspaceForDoc(doc);
                                        }}
                                        title="Open in workspace (highlight-to-explain)"
                                      >
                                        <BookOpen size={10} />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      className={styles.removeDocBtn}
                                      onClick={() =>
                                        setAttachedDocuments((prev) =>
                                          prev.filter((_, i) => i !== index),
                                        )
                                      }
                                      title="Remove document"
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          <textarea
                            ref={textareaRef}
                            className={styles.chatInputField}
                            placeholder="Ask Lumi"
                            value={input}
                            onChange={(e) => {
                              setInput(e.target.value);
                              queueResize(e.target);
                            }}
                            onKeyDown={handleKeyPress}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() =>
                              setTimeout(() => setIsInputFocused(false), 180)
                            }
                            rows="1"
                            autoFocus={!isMobile}
                            aria-label="Chat input"
                          />
                          {isStreaming ? (
                            <button
                              type="button"
                              className={`${styles.sendBtn} ${styles.stopBtn}`}
                              onClick={(e) => {
                                e.preventDefault();
                                handleStopStreaming();
                              }}
                              aria-label="Stop generation"
                            >
                              <Square
                                size={16}
                                className={iconStyles.iconPulse}
                              />
                            </button>
                          ) : (
                            <LiquidMetalButton
                              onClick={undefined}
                              disabled={!input.trim()}
                              isActive={input.trim().length > 0}
                            />
                          )}
                        </div>
                        <div className={styles.inputOptionsContainer}>
                          <div
                            className={styles.formatDropdown}
                            ref={docContextDropdownRef}
                          >
                            <button
                              type="button"
                              className={styles.addContextBtn}
                              onClick={() =>
                                setShowDocContextDropdown(
                                  !showDocContextDropdown,
                                )
                              }
                            >
                              <Paperclip size={16} />
                              <span>Add Context</span>
                            </button>
                          </div>
                          <button
                            type="button"
                            className={styles.attachBtn}
                            onClick={() => fileInputRef.current?.click()}
                            title="Attach file"
                          >
                            <Paperclip size={18} />
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Quick Start Dropdown */}
                    <div
                      className={`${styles.quickStartDrop} ${
                        isInputFocused && !input.trim()
                          ? styles.quickStartDropOpen
                          : ""
                      }`}
                      role="listbox"
                      aria-label="Quick start suggestions"
                      aria-hidden={!isInputFocused}
                    >
                      <p className={styles.quickStartLabel}>
                        Quick starts
                        {activeNote?.title && (
                          <span className={styles.quickStartContext}>
                            {" · "}
                            <span className={styles.quickStartContextBadge}>
                              {activeNote.title}
                            </span>
                          </span>
                        )}
                      </p>
                      <div className={styles.quickStartChips}>
                        {[
                          {
                            emoji: "📄",
                            label: activeNote
                              ? `Summarize "${activeNote.title}"`
                              : "Summarize this note",
                            prompt: "Summarize the key points clearly.",
                          },
                          {
                            emoji: "❓",
                            label: "Make a 5-question quiz",
                            prompt: "Create a 5-question multiple-choice quiz.",
                          },
                          {
                            emoji: "💡",
                            label: "Explain key concepts",
                            prompt: "Explain the key concepts clearly.",
                          },
                        ].map(({ emoji, label, prompt }) => (
                          <button
                            key={label}
                            className={styles.quickStartChip}
                            role="option"
                            aria-selected="false"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setIsInputFocused(false);
                              // Inject active-note context when available so
                              // the AI summarises / quizzes / explains THIS note
                              const ctx = getActiveNoteContext(4000);
                              const finalPrompt = ctx
                                ? `${prompt}\n\n---\nNOTE: ${activeNote?.title || "Untitled"}\n---\n${ctx}`
                                : prompt;
                              handleSendMessage(null, finalPrompt);
                            }}
                            aria-label={label}
                          >
                            <span
                              className={styles.quickStartChipEmoji}
                              aria-hidden="true"
                            >
                              {emoji}
                            </span>
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </EmptyState>
              </div>
            ) : null}

            {/* ── Document Insights Card ────────────────────────────── */}
            {docInsights && !isCollapsed && (
              <div
                style={{
                  margin: "0 16px 10px",
                  padding: "12px 14px",
                  background: "rgba(99,102,241,0.07)",
                  border: "1px solid rgba(99,102,241,0.2)",
                  borderRadius: "12px",
                  fontSize: "12px",
                  position: "relative",
                }}
              >
                <button
                  onClick={() => setDocInsights(null)}
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "10px",
                    background: "none",
                    border: "none",
                    color: "rgba(255,255,255,0.3)",
                    cursor: "pointer",
                    fontSize: "16px",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
                {docInsights.loading ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: "rgba(255,255,255,0.5)",
                    }}
                  >
                    <div
                      style={{
                        width: "14px",
                        height: "14px",
                        border: "2px solid rgba(99,102,241,0.4)",
                        borderTopColor: "#818cf8",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                    Analyzing {docInsights.docName}…
                  </div>
                ) : docInsights.data ? (
                  <div>
                    <div
                      style={{
                        fontWeight: 700,
                        color: "#818cf8",
                        marginBottom: "6px",
                        paddingRight: "18px",
                      }}
                    >
                      📄 {docInsights.data.topic || docInsights.docName}
                      <span
                        style={{
                          fontWeight: 400,
                          color: "rgba(255,255,255,0.4)",
                          marginLeft: "8px",
                        }}
                      >
                        {docInsights.data.page_count} pp · ~
                        {docInsights.data.estimated_read_time} min read ·{" "}
                        {docInsights.data.difficulty}
                      </span>
                    </div>
                    <div
                      style={{
                        color: "rgba(255,255,255,0.65)",
                        marginBottom: "8px",
                        lineHeight: 1.5,
                      }}
                    >
                      {docInsights.data.summary}
                    </div>
                    {(docInsights.data.key_concepts || []).length > 0 && (
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          marginBottom: "8px",
                        }}
                      >
                        {docInsights.data.key_concepts
                          .slice(0, 5)
                          .map((c, i) => (
                            <span
                              key={i}
                              style={{
                                padding: "2px 8px",
                                background: "rgba(99,102,241,0.15)",
                                border: "1px solid rgba(99,102,241,0.25)",
                                borderRadius: "20px",
                                color: "#a5b4fc",
                                fontSize: "11px",
                              }}
                            >
                              {c}
                            </span>
                          ))}
                      </div>
                    )}
                    <div
                      style={{ display: "flex", gap: "6px", marginTop: "6px" }}
                    >
                      <button
                        onClick={() =>
                          handleSendMessage(
                            null,
                            `Create a quiz about: ${docInsights.data.topic || docInsights.docName}`,
                            "Quiz",
                          )
                        }
                        style={{
                          padding: "4px 10px",
                          borderRadius: "6px",
                          border: "1px solid rgba(139,92,246,0.3)",
                          background: "rgba(139,92,246,0.1)",
                          color: "#c084fc",
                          cursor: "pointer",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        Quiz Me
                      </button>
                      <button
                        onClick={() =>
                          handleSendMessage(
                            null,
                            `Create flashcards for: ${docInsights.data.topic || docInsights.docName}`,
                            "Flashcards",
                          )
                        }
                        style={{
                          padding: "4px 10px",
                          borderRadius: "6px",
                          border: "1px solid rgba(52,211,153,0.3)",
                          background: "rgba(52,211,153,0.08)",
                          color: "#34d399",
                          cursor: "pointer",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        Flashcards
                      </button>
                      <button
                        onClick={() =>
                          handleSendMessage(
                            null,
                            `Summarize the key concepts from: ${docInsights.data.topic || docInsights.docName}`,
                          )
                        }
                        style={{
                          padding: "4px 10px",
                          borderRadius: "6px",
                          border: "1px solid rgba(251,191,36,0.3)",
                          background: "rgba(251,191,36,0.08)",
                          color: "#fbbf24",
                          cursor: "pointer",
                          fontSize: "11px",
                          fontWeight: 600,
                        }}
                      >
                        Summarize
                      </button>
                    </div>
                    {(docInsights.data.potential_exam_topics || []).length >
                      0 && (
                      <div
                        style={{
                          marginTop: "8px",
                          color: "rgba(255,255,255,0.4)",
                          fontSize: "11px",
                        }}
                      >
                        Likely exam topics:{" "}
                        {docInsights.data.potential_exam_topics.join(" · ")}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            <div
              className={`${styles.chatInputControls} ${
                messages.length === 0 ? styles.hidden : ""
              } ${!isCollapsed ? styles.chatInputControlsWithPanel : ""}`}
            >
              {/* Hidden file input for direct upload */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx,.txt,.md"
                multiple
                style={{ display: "none" }}
              />

              <div
                id="chatInputBar"
                className={`${styles.chatInputBar} ${
                  !isCollapsed ? styles.chatInputBarResponsive : ""
                } ${input.trim() ? styles.active : ""} ${
                  isInputFocused ? styles.chatInputBarFocused : ""
                }`}
              >
                <form onSubmit={handleSendMessage} className={styles.chatForm}>
                  <div className={styles.inputWrapper}>
                    {/* Attached Documents Display - Inside Input */}
                    {attachedDocuments && attachedDocuments.length > 0 && (
                      <div className={styles.attachedDocsInline}>
                        {(attachedDocuments || []).map((doc, index) => (
                          <div
                            key={doc.id || doc.name || index}
                            className={`${styles.attachedDocChip} ${
                              doc.isIndexing
                                ? styles.attachedDocChipIndexing
                                : ""
                            }`}
                            title={
                              doc.isIndexing
                                ? "Indexing… (ready to chat once done)"
                                : doc.indexError
                                  ? `Indexing failed: ${doc.indexError}`
                                  : doc.name
                            }
                          >
                            {doc.isIndexing ? (
                              <Loader2
                                size={12}
                                className={styles.attachedDocSpinner}
                              />
                            ) : (
                              <FileText size={12} />
                            )}
                            <span className={styles.attachedDocName}>
                              {(() => {
                                const n = doc?.name || "Untitled";
                                return n.length > 20
                                  ? n.substring(0, 17) + "..."
                                  : n;
                              })()}
                            </span>
                            {(doc.type === "application/pdf" ||
                              doc.name?.toLowerCase().endsWith(".pdf")) && (
                              <button
                                type="button"
                                className={styles.workspaceDocBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openWorkspaceForDoc(doc);
                                }}
                                title="Open in workspace (highlight-to-explain)"
                              >
                                <BookOpen size={10} />
                              </button>
                            )}
                            <button
                              type="button"
                              className={styles.removeDocBtn}
                              onClick={() =>
                                setAttachedDocuments((prev) =>
                                  prev.filter((_, i) => i !== index),
                                )
                              }
                              title="Remove document"
                            >
                              <X size={10} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <textarea
                      ref={textareaRef}
                      className={styles.chatInputField}
                      placeholder={
                        attachedDocuments && attachedDocuments.length > 0
                          ? `Ask about ${attachedDocuments.length} document${
                              attachedDocuments.length > 1 ? "s" : ""
                            }...`
                          : "Ask anything..."
                      }
                      value={input}
                      onChange={(e) => {
                        setInput(e.target.value);
                        queueResize(e.target);
                      }}
                      onKeyDown={handleKeyPress}
                      onFocus={() => setIsInputFocused(true)}
                      onBlur={() =>
                        setTimeout(() => setIsInputFocused(false), 180)
                      }
                      rows="1"
                      autoFocus={!isMobile}
                      aria-label="Chat input"
                    />
                    {isStreaming ? (
                      <button
                        type="button"
                        className={`${styles.sendBtn} ${styles.stopBtn}`}
                        onClick={(e) => {
                          e.preventDefault();
                          handleStopStreaming();
                        }}
                        aria-label="Stop generation"
                      >
                        <Square size={16} className={iconStyles.iconPulse} />
                      </button>
                    ) : (
                      <LiquidMetalButton
                        onClick={undefined}
                        disabled={!input.trim()}
                        isActive={input.trim().length > 0}
                      />
                    )}
                  </div>
                  <div className={styles.inputOptionsContainer}>
                    {/* Document Context Selector */}
                    <div
                      className={styles.formatDropdown}
                      ref={docContextDropdownRef}
                    >
                      <button
                        type="button"
                        className={styles.formatSelector}
                        onClick={() => {
                          if (showDocContextDropdown) {
                            setIsClosing(true);
                            setTimeout(() => {
                              setShowDocContextDropdown(false);
                              setIsClosing(false);
                            }, 150);
                          } else {
                            setShowDocContextDropdown(true);
                            setIsClosing(false);
                          }
                        }}
                      >
                        <FileText
                          size={14}
                          className={iconStyles.iconHoverGrow}
                        />
                        {attachedDocuments.length > 0
                          ? `Context (${attachedDocuments.length})`
                          : "Add Context"}
                        <ChevronDown
                          size={14}
                          className={`${iconStyles.iconColorTransition} ${
                            styles.dropdownArrow
                          } ${
                            showDocContextDropdown
                              ? styles.dropdownArrowOpen
                              : ""
                          }`}
                        />
                      </button>
                      {(showDocContextDropdown || isClosing) && (
                        <div
                          className={`${styles.formatDropdownMenu} ${
                            showDocContextDropdown && !isClosing
                              ? styles.open
                              : ""
                          } ${isClosing ? styles.closing : ""}`}
                        >
                          <div className={styles.dropdownHeader}>
                            <span>Select documents for context</span>
                          </div>
                          {availableDocuments &&
                            availableDocuments.length > 0 &&
                            (availableDocuments || [])
                              .slice(0, 5)
                              .map((doc) => {
                                const isAttached =
                                  attachedDocuments &&
                                  attachedDocuments.some(
                                    (d) =>
                                      d.id === doc.id || d.name === doc.name,
                                  );
                                // The picker may show a stale copy from
                                // availableDocuments. If this doc IS attached,
                                // prefer the live attached version so we pick
                                // up isIndexing / extractedText flags.
                                const liveDoc = isAttached
                                  ? attachedDocuments.find(
                                      (d) =>
                                        d.id === doc.id || d.name === doc.name,
                                    ) || doc
                                  : doc;
                                const isPdf =
                                  liveDoc.type === "application/pdf" ||
                                  liveDoc.name?.toLowerCase().endsWith(".pdf");
                                const toggleAttach = () => {
                                  if (isAttached) {
                                    setAttachedDocuments((prev) =>
                                      prev.filter(
                                        (d) =>
                                          d.id !== doc.id &&
                                          d.name !== doc.name,
                                      ),
                                    );
                                  } else {
                                    attachDocWithIndexing(doc);
                                  }
                                };
                                return (
                                  <div
                                    key={doc.id || doc.name}
                                    role="button"
                                    tabIndex={0}
                                    className={`${styles.formatDropdownItem} ${
                                      isAttached
                                        ? styles.formatDropdownItemActive
                                        : ""
                                    }`}
                                    onClick={toggleAttach}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        toggleAttach();
                                      }
                                    }}
                                    title={
                                      liveDoc.isIndexing
                                        ? "Indexing… (ready to chat once done)"
                                        : liveDoc.indexError
                                          ? `Indexing failed: ${liveDoc.indexError}`
                                          : undefined
                                    }
                                  >
                                    {liveDoc.isIndexing ? (
                                      <Loader2
                                        size={14}
                                        className={styles.attachedDocSpinner}
                                      />
                                    ) : (
                                      <FileText
                                        size={14}
                                        className={iconStyles.iconHoverGrow}
                                      />
                                    )}
                                    <span className={styles.docName}>
                                      {liveDoc.name}
                                    </span>
                                    {isPdf && isAttached && (
                                      <button
                                        type="button"
                                        className={styles.workspaceDocBtn}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openWorkspaceForDoc(liveDoc);
                                        }}
                                        title="Open in workspace (highlight-to-explain)"
                                      >
                                        <BookOpen size={12} />
                                      </button>
                                    )}
                                    {isAttached && (
                                      <Check
                                        size={14}
                                        className={`${styles.checkIcon} ${iconStyles.iconScaleIn}`}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                          {availableDocuments &&
                            availableDocuments.length > 5 && (
                              <div
                                className={styles.formatDropdownItem}
                                style={{
                                  opacity: 0.45,
                                  cursor: "default",
                                  fontSize: "0.78rem",
                                }}
                              >
                                +{availableDocuments.length - 5} more — open
                                Document Library to see all
                              </div>
                            )}
                          {availableDocuments &&
                            availableDocuments.length === 0 && (
                              <div
                                className={styles.formatDropdownItem}
                                style={{ opacity: 0.6, cursor: "default" }}
                              >
                                <FileText
                                  size={14}
                                  className={iconStyles.iconHoverGrow}
                                />{" "}
                                No documents uploaded
                              </div>
                            )}
                          <button
                            type="button"
                            className={styles.formatDropdownItem}
                            onClick={() => {
                              // Open Document Library modal
                              setShowDocumentLibrary(true);
                              setIsClosing(true);
                              setTimeout(() => {
                                setShowDocContextDropdown(false);
                                setIsClosing(false);
                              }, 150);
                            }}
                          >
                            <Paperclip
                              size={16}
                              className={iconStyles.iconHoverGrow}
                            />{" "}
                            Upload Document
                          </button>
                          {attachedDocuments &&
                            attachedDocuments.length > 0 && (
                              <button
                                type="button"
                                className={styles.formatDropdownItem}
                                onClick={() => {
                                  setAttachedDocuments([]);
                                  setIsClosing(true);
                                  setTimeout(() => {
                                    setShowDocContextDropdown(false);
                                    setIsClosing(false);
                                  }, 150);
                                }}
                              >
                                <X
                                  size={12}
                                  className={iconStyles.iconHoverGrow}
                                />{" "}
                                Clear all context
                              </button>
                            )}
                        </div>
                      )}
                    </div>

                    {/* File attachment button */}
                    <button
                      type="button"
                      className={styles.optionButton}
                      onClick={handleFileAttachment}
                      aria-label="Upload documents"
                      title="Upload documents"
                    >
                      <Paperclip
                        size={18}
                        className={iconStyles.iconHoverGrow}
                      />
                    </button>
                  </div>
                </form>
              </div>

              {/* Quick Actions - always visible */}
              <div className={styles.quickActionsRow}>
                <div className={styles.quickActionWrapper}>
                  <button
                    className={styles.quickActionBtn}
                    onClick={() => setShowAssignmentBuilder(true)}
                  >
                    {renderIcon("add-circle")}
                    <span>Assignment</span>
                  </button>
                  <div className={styles.quickActionTooltip}>
                    Generate complete assignments with AI assistance
                  </div>
                </div>
                <div className={styles.quickActionWrapper}>
                  <button
                    className={styles.quickActionBtn}
                    onClick={() =>
                      handlePromptClick("Create a quiz about this topic")
                    }
                  >
                    {renderIcon("chat-bubble")}
                    <span>Quiz</span>
                  </button>
                  <div className={styles.quickActionTooltip}>
                    Generate practice quizzes from your documents
                  </div>
                </div>
                <div className={styles.quickActionWrapper}>
                  <button
                    className={styles.quickActionBtn}
                    onClick={() =>
                      handlePromptClick("Create flashcards for studying")
                    }
                  >
                    {renderIcon("upload-cloud")}
                    <span>Flashcards</span>
                  </button>
                  <div className={styles.quickActionTooltip}>
                    Create flashcards for memorization
                  </div>
                </div>
                <div className={styles.quickActionWrapper}>
                  <button
                    className={styles.quickActionBtn}
                    onClick={() =>
                      handlePromptClick("Create study notes from this content")
                    }
                  >
                    {renderIcon("file-check")}
                    <span>Notes</span>
                  </button>
                  <div className={styles.quickActionTooltip}>
                    Generate organized study notes
                  </div>
                </div>
                <div className={styles.quickActionWrapper}>
                  <button
                    ref={layoutBtnRef}
                    className={`${styles.quickActionBtn} ${styles.layoutBtn} ${showLayoutSelector ? styles.quickActionBtnActive : ""}`}
                    onClick={() => setShowLayoutSelector((v) => !v)}
                  >
                    <Grid size={18} className={iconStyles.iconHoverGrow} />
                    <span>Layout</span>
                  </button>
                  <LayoutSelector
                    isOpen={showLayoutSelector}
                    onClose={() => setShowLayoutSelector(false)}
                    anchorEl={layoutBtnRef.current}
                  />
                </div>
              </div>
            </div>
            <div className={styles.disclaimerText}>
              Check responses for accuracy.
            </div>
          </div>
        </div>
      </div>

      {activeModal && (
        <Modal
          isOpen={!!activeModal}
          onClose={closeModal}
          title="Feature Modal"
          isPremiumFeature={false}
          isUserPremium={isUserPremium}
        >
          <div>Modal content would go here</div>
        </Modal>
      )}

      <AssignmentSetupModal
        isOpen={showAssignmentModal}
        onClose={() => setShowAssignmentModal(false)}
        documents={availableDocuments}
        onSubmit={({ title, assignmentDocId, referenceDocIds, notes }) => {
          const groupId = createGroup(title || "Assignment Group");
          setActiveGroup(groupId);
          if (assignmentDocId) setAssignmentDoc(groupId, assignmentDocId);
          (referenceDocIds || []).forEach((rid) =>
            addReferenceDoc(groupId, rid),
          );
          setShowAssignmentModal(false);
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now() + 1,
              type: "assistant",
              content: `Assignment group "${title}" created.${
                assignmentDocId ? " Assignment doc set." : ""
              } ${
                referenceDocIds?.length
                  ? referenceDocIds.length + " references added."
                  : ""
              }`.trim(),
            },
          ]);
        }}
      />

      <TestParameterModal
        isOpen={showTestParams}
        onClose={() => setShowTestParams(false)}
        onConfirm={handleConfirmTestParams}
      />
      {/* Immersive Assignment Builder - Side Panel */}
      <AssignmentBuilder
        isOpen={showAssignmentBuilder}
        onClose={() => setShowAssignmentBuilder(false)}
        attachedDocs={attachedDocuments}
      />

      {/* Immersive Test Component */}
      {showTest && (
        <TestContainer defaultTopic="" onExit={() => setShowTest(false)} />
      )}

      {/* Document Library */}
      {showDocumentLibrary && (
        <DocumentLibrary
          onClose={() => setShowDocumentLibrary(false)}
          onGenerateFromDoc={(type, data, docName) => {
            // Handle generated content from document
            if (type === "quiz") {
              // Extract quiz data - handle both formats
              const quizData = data.result || data;

              // Add user request message
              const userMessage = {
                id: Date.now() - 1,
                type: "user",
                content: `Generate a quiz from "${docName}"`,
                timestamp: new Date().toISOString(),
              };

              // Add assistant response with quiz
              const quizMessage = {
                id: Date.now(),
                type: "assistant",
                content: `\`\`\`quiz\n${JSON.stringify(
                  quizData,
                  null,
                  2,
                )}\n\`\`\``,
                timestamp: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, userMessage, quizMessage]);

              // Quiz is in the chat message above — user opens via "Start Quiz" card
            } else if (type === "test") {
              // Extract test data - handle both formats
              const testData = data.result || data.test || data;

              // Add user request message
              const userMessage = {
                id: Date.now() - 1,
                type: "user",
                content: `Generate a test from "${docName}"`,
                timestamp: new Date().toISOString(),
              };

              // Add assistant response with test
              const testMessage = {
                id: Date.now(),
                type: "assistant",
                content: `\`\`\`test\n${JSON.stringify(
                  testData,
                  null,
                  2,
                )}\n\`\`\``,
                timestamp: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, userMessage, testMessage]);

              // Also dispatch event to open in side panel
              const testEvent = new CustomEvent("openTest", {
                detail: {
                  test: testData,
                  title: `Test from ${docName}`,
                },
              });
              window.dispatchEvent(testEvent);
            } else if (type === "flashcards") {
              // Extract flashcards data - handle both formats
              const flashcardsData = data.result || data;

              // Add user request message
              const userMessage = {
                id: Date.now() - 1,
                type: "user",
                content: `Generate flashcards from "${docName}"`,
                timestamp: new Date().toISOString(),
              };

              // Add assistant response with flashcards
              const flashcardsMessage = {
                id: Date.now(),
                type: "assistant",
                content: `\`\`\`flashcards\n${JSON.stringify(
                  flashcardsData,
                  null,
                  2,
                )}\n\`\`\``,
                timestamp: new Date().toISOString(),
              };
              setMessages((prev) => [...prev, userMessage, flashcardsMessage]);

              // Also dispatch event to open in side panel
              const flashcardEvent = new CustomEvent("openFlashcards", {
                detail: {
                  flashcards: flashcardsData,
                  title: `Flashcards from ${docName}`,
                },
              });
              window.dispatchEvent(flashcardEvent);
            } else if (type === "notes") {
              // Extract notes content - handle both formats
              const notesContent =
                data.result ||
                data.notes ||
                (typeof data === "string" ? data : JSON.stringify(data));

              // Add to chat with interactive buttons (like quiz)
              const userMessage = {
                id: Date.now() - 1,
                type: "user",
                content: `Generate study notes from "${docName}"`,
                timestamp: new Date().toISOString(),
              };

              const notesMessage = {
                id: Date.now(),
                type: "assistant",
                content: notesContent,
                notesData: notesContent, // Store for immersive view
                notesTitle: `Study Notes - ${docName}`,
                documentInfo: {
                  name: docName,
                  size: data.docSize || "Document",
                  pages: data.pages,
                  type: data.fileType || "PDF",
                },
                timestamp: new Date().toISOString(),
              };

              setMessages((prev) => [...prev, userMessage, notesMessage]);
            } else if (type === "quotes") {
              // Extract quotes data - handle both formats
              let quotesData = data.result || data;

              // Parse JSON string if needed
              if (typeof quotesData === "string") {
                try {
                  quotesData = JSON.parse(quotesData);
                } catch (e) {
                  console.error("Failed to parse quotes JSON:", e);
                }
              }

              // Add to chat with interactive button (like notes)
              const userMessage = {
                id: Date.now() - 1,
                type: "user",
                content: `Generate key quotes from "${docName}"`,
                timestamp: new Date().toISOString(),
              };

              const quotesMessage = {
                id: Date.now(),
                type: "assistant",
                content: `Generated ${
                  quotesData?.quotes?.length || 0
                } key quotes`,
                quotesData: quotesData,
                documentName: docName,
                documentInfo: {
                  name: docName,
                  size: data.docSize || "Document",
                  pages: data.pages,
                  type: data.fileType || "PDF",
                },
                timestamp: new Date().toISOString(),
              };

              setMessages((prev) => [...prev, userMessage, quotesMessage]);
            }
          }}
        />
      )}

      {/* Study Tool Source Selection Modal */}
      {showStudyToolModal && (
        <div
          className={styles.studyToolModalOverlay}
          onClick={() => setShowStudyToolModal(false)}
        >
          <motion.div
            className={styles.studyToolModal}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.studyToolModalHeader}>
              <h3>
                {selectedStudyTool === "quiz" && "Generate Quiz"}
                {selectedStudyTool === "flashcards" && "Create Flashcards"}
                {selectedStudyTool === "notes" && "Generate Study Notes"}
              </h3>
              <button
                className={styles.studyToolModalClose}
                onClick={() => setShowStudyToolModal(false)}
              >
                <X size={18} />
              </button>
            </div>
            <p className={styles.studyToolModalSubtitle}>
              Choose how you'd like to create your {selectedStudyTool}
            </p>
            <div className={styles.studyToolOptions}>
              <button
                className={styles.studyToolOption}
                onClick={() => {
                  setShowStudyToolModal(false);
                  setShowDocumentLibrary(true);
                }}
              >
                <div className={styles.studyToolOptionIcon}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M16 13H8" />
                    <path d="M16 17H8" />
                  </svg>
                </div>
                <div className={styles.studyToolOptionContent}>
                  <span className={styles.studyToolOptionTitle}>
                    From Document
                  </span>
                  <span className={styles.studyToolOptionDesc}>
                    Upload or select a document from your library
                  </span>
                </div>
                <svg
                  className={styles.studyToolOptionArrow}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
              <button
                className={styles.studyToolOption}
                onClick={() => {
                  setShowStudyToolModal(false);
                  const prompts = {
                    quiz: "Create a quiz about ",
                    flashcards: "Create flashcards for ",
                    notes: "Generate study notes about ",
                  };
                  setInput(prompts[selectedStudyTool] || "");
                  // Focus the input
                  setTimeout(() => {
                    const inputEl = document.querySelector(
                      'textarea[placeholder="Ask Lumi"]',
                    );
                    if (inputEl) inputEl.focus();
                  }, 100);
                }}
              >
                <div className={styles.studyToolOptionIcon}>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    <path d="M8 9h8" />
                    <path d="M8 13h6" />
                  </svg>
                </div>
                <div className={styles.studyToolOptionContent}>
                  <span className={styles.studyToolOptionTitle}>
                    From Chat Input
                  </span>
                  <span className={styles.studyToolOptionDesc}>
                    Type a topic or paste content directly
                  </span>
                </div>
                <svg
                  className={styles.studyToolOptionArrow}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Command Palette */}
      <CommandPalette
        isOpen={showCommandPalette}
        onClose={() => setShowCommandPalette(false)}
        onCommand={handleCommand}
      />

      {/* Document Picker Modal */}
      {showDocumentPicker && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 150,
              backdropFilter: "blur(4px)",
            }}
            onClick={() => setShowDocumentPicker(false)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "var(--bg-primary)",
              border: "1px solid var(--border)",
              borderRadius: "12px",
              padding: "20px",
              maxWidth: "500px",
              width: "90%",
              maxHeight: "70vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              zIndex: 151,
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>
                Attach Documents
              </h3>
              <button
                onClick={() => setShowDocumentPicker(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "var(--foreground)",
                  fontSize: "1.5rem",
                  cursor: "pointer",
                  padding: "0",
                  width: "28px",
                  height: "28px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "6px",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.background = "rgba(255, 255, 255, 0.1)")
                }
                onMouseLeave={(e) => (e.target.style.background = "none")}
              >
                ×
              </button>
            </div>

            {availableDocuments && availableDocuments.length > 0 ? (
              (availableDocuments || []).map((doc, idx) => {
                const isSelected =
                  attachedDocuments &&
                  attachedDocuments.some((d) => d.name === doc.name);
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      if (isSelected) {
                        setAttachedDocuments((prev) =>
                          prev.filter((d) => d.name !== doc.name),
                        );
                      } else {
                        attachDocWithIndexing(doc);
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "12px",
                      background: isSelected
                        ? "var(--foreground)"
                        : "var(--bg-secondary)",
                      color: isSelected
                        ? "var(--background)"
                        : "var(--foreground)",
                      border: `1px solid ${
                        isSelected ? "var(--foreground)" : "var(--border)"
                      }`,
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      textAlign: "left",
                      fontWeight: isSelected ? 600 : 400,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.background = "rgba(255, 255, 255, 0.05)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.background = "var(--bg-secondary)";
                      }
                    }}
                  >
                    <div
                      style={{
                        fontSize: "1.2rem",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isSelected ? (
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            background: "var(--foreground)",
                            color: "var(--background)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          ✓
                        </div>
                      ) : (
                        <FileText size={18} />
                      )}
                    </div>
                    <div style={{ flex: 1, overflow: "hidden" }}>
                      <div
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontSize: "0.9rem",
                        }}
                      >
                        {doc.name}
                      </div>
                      {doc.size && (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            opacity: 0.6,
                            marginTop: "2px",
                          }}
                        >
                          {doc.size}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            ) : (
              <div
                style={{
                  padding: "40px 20px",
                  textAlign: "center",
                  color: "var(--foreground)",
                  opacity: 0.6,
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "12px" }}>
                  <FileText size={32} />
                </div>
                <div>No documents uploaded yet</div>
                <div style={{ fontSize: "0.85rem", marginTop: "8px" }}>
                  Upload documents in the Document Library
                </div>
              </div>
            )}

            <div
              style={{
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid var(--border)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: "0.85rem", opacity: 0.7 }}>
                {attachedDocuments.length} selected
              </div>
              <button
                onClick={() => setShowDocumentPicker(false)}
                style={{
                  background: "var(--foreground)",
                  color: "var(--background)",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  cursor: "pointer",
                  transition: "opacity 0.2s",
                }}
                onMouseEnter={(e) => (e.target.style.opacity = "0.9")}
                onMouseLeave={(e) => (e.target.style.opacity = "1")}
              >
                Done
              </button>
            </div>
          </motion.div>
        </>
      )}

      {/* Content Side Panel - All generated content (notes, quotes, quizzes, flashcards) */}
      <AnimatePresence>
        {!shellEmbedded && showContentPanel && contentPanelTabs.length > 0 && (
          <ContentSidePanel
            tabs={contentPanelTabs}
            activeTabIndex={activeContentTab}
            onTabChange={setActiveContentTab}
            onClose={() => {
              setShowContentPanel(false);
              setContentPanelTabs([]);
              setActiveContentTab(0);
            }}
            onCloseTab={(index) => {
              const newTabs = contentPanelTabs.filter((_, i) => i !== index);
              setContentPanelTabs(newTabs);

              // Adjust active tab if needed
              if (newTabs.length === 0) {
                setShowContentPanel(false);
                setActiveContentTab(0);
              } else if (activeContentTab >= newTabs.length) {
                setActiveContentTab(newTabs.length - 1);
              } else if (activeContentTab > index) {
                setActiveContentTab(activeContentTab - 1);
              }
            }}
            onMinimize={() =>
              setIsContentPanelMinimized(!isContentPanelMinimized)
            }
            isMinimized={isContentPanelMinimized}
          />
        )}
      </AnimatePresence>

      {/* Notes overlay — slides in over chat, no unmount of chat view */}
      <AnimatePresence mode="wait">
        {viewMode === "notes" && (
          <motion.div
            key="notes-overlay"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              mass: 0.8,
            }}
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              left: isMobile ? 0 : sidebarOpen ? 280 : 72,
              zIndex: 300,
              background: "var(--bg-primary)",
              overflowY: "auto",
              transition: "left 0.25s ease-in-out",
            }}
          >
            <NotebookPage
              onBackToChat={() => switchViewMode("chat")}
              hideSidebar
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Study Workspace Canvas — only shown when not embedded in AppShell */}
      {!shellEmbedded && (
        <AnimatePresence>
          {isCanvasOpen && (
            <DocumentCanvas
              documents={activeWorkspace?.documents || []}
              onClose={closeCanvas}
              onAskLumi={(text) => {
                closeCanvas();
                setTimeout(() => handleSendMessage(null, text), 100);
              }}
            />
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

export default React.memo(HomePage);
